import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  X, 
  FileEdit,
  RefreshCw,
  Upload,
  Image as ImageIcon,
  Download,
  Maximize,
  Minimize2,
  Mail,
  Trash,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Calendar as CalendarIcon,
  MessageSquare,
  Send,
  Wand2,
  Layers,
  AlertTriangle,
  RotateCcw,
  FolderOpen,
  FolderPlus,
  Tag,
  Plus,
  Pencil,
  FolderInput,
  Maximize2,
} from 'lucide-react';
import { BlogEditor, blogHtmlToText } from '../../components/BlogEditor';
import { RichTextFieldEditor, richFieldHtmlToText } from '../../components/RichTextFieldEditor';
import { 
  FaLinkedin, FaInstagram, 
  FaTwitter, FaPinterest, FaBlog, FaEnvelope, FaNewspaper, FaFacebook, FaMedium, FaYoutube
} from 'react-icons/fa';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

// Showcase: kein echter API-Endpoint
const API = '';

// Platform configurations - updated February 2026
const PLATFORM_CONFIG = {
  x: { 
    name: 'X', icon: FaTwitter, color: '#000000', 
    maxLength: 280, hasHeadline: false, headlineMax: 0, 
    hashtags: 3, imageLimit: 1, image: '1200×675 (16:9)',
    note: 'Hashtags zählen in die 280 Zeichen!'
  },
  linkedin: { 
    name: 'LinkedIn Beitrag', icon: FaLinkedin, color: '#0077B5', 
    maxLength: 3000, hasHeadline: false, headlineMax: 0, 
    hashtags: 5, imageLimit: 20, image: '1200×627 (1.91:1)'
  },
  instagram: { 
    name: 'Instagram', icon: FaInstagram, color: '#E4405F', 
    maxLength: 2200, hasHeadline: false, headlineMax: 0, 
    hashtags: 5, imageLimit: 10, image: '1080×1350 (4:5)'
  },
  facebook: { 
    name: 'Facebook', icon: FaFacebook, color: '#1877F2', 
    maxLength: 5000, hasHeadline: false, headlineMax: 0, 
    hashtags: 3, imageLimit: 10, image: '1200×630 (1.91:1)'
  },
  pinterest: { 
    name: 'Pinterest', icon: FaPinterest, color: '#BD081C', 
    maxLength: 320, hasHeadline: true, headlineMax: 28, 
    hashtags: 5, imageLimit: 5, image: '1000×1500 (2:3)'
  },
  medium: { 
    name: 'Medium', icon: FaMedium, color: '#000000', 
    maxLength: 4000, hasHeadline: true, headlineMax: 67, 
    hashtags: 0, imageLimit: 10, image: '1200×628 (1.91:1)'
  },
  blog: { 
    name: 'Blogartikel', icon: FaBlog, color: '#00D9C8', 
    maxLength: 2800, hasHeadline: true, headlineMax: 60, 
    hashtags: 0, imageLimit: 10, image: '1200×628 (1.91:1)'
  },
  newsletter: { 
    name: 'Newsletter', icon: FaEnvelope, color: '#F59E0B', 
    maxLength: 600, hasHeadline: true, headlineMax: 70, 
    hashtags: 0, imageLimit: 0, image: null
  },
  pressemitteilung: { 
    name: 'Pressemitteilung', icon: FaNewspaper, color: '#6B7280', 
    maxLength: 5000, hasHeadline: true, headlineMax: 57, 
    hashtags: 0, imageLimit: 1, image: '1200×628 (1.91:1)',
    structured: true
  },
  youtube: {
    name: 'YouTube', icon: FaYoutube, color: '#FF0000',
    maxLength: 450, hasHeadline: true, headlineMax: 100,
    hashtags: 0, imageLimit: 1, image: '1920×1080 (16:9)',
    structured: true, isVideo: true
  },
  // Legacy alias
  twitter: { 
    name: 'X', icon: FaTwitter, color: '#000000', 
    maxLength: 280, hasHeadline: false, headlineMax: 0, 
    hashtags: 3, imageLimit: 4, image: '1200×675 (16:9)'
  }
};

// Platform list for filter dropdown
const platforms = Object.entries(PLATFORM_CONFIG)
  .filter(([key]) => key !== 'twitter') // Exclude legacy alias
  .map(([key, config]) => ({
    key,
    label: config.name,
    icon: config.icon,
    color: config.color,
    limit: config.maxLength
  }));

// Pressemitteilung field configuration
const PRESS_RELEASE_FIELDS_DE = [
  { key: 'headline', label: 'Überschrift', max: 57 },
  { key: 'subtitle', label: 'Untertitel', max: 150, multiline: true },
  { key: 'short_text', label: 'Kurztext zur Meldung', max: 800, multiline: true },
  { key: 'body_text', label: 'Meldungstext', max: 3200, multiline: true },
  { key: 'keywords', label: 'Schlüsselwörter', max: 200, optional: true, multiline: true },
  { key: 'company_description', label: 'Firmenbeschreibung', max: 800, multiline: true },
];
const PRESS_RELEASE_FIELDS_EN = [
  { key: 'headline', label: 'Headline', max: 57 },
  { key: 'subtitle', label: 'Subtitle', max: 150, multiline: true },
  { key: 'short_text', label: 'Short Summary', max: 800, multiline: true },
  { key: 'body_text', label: 'Body Text', max: 3200, multiline: true },
  { key: 'keywords', label: 'Keywords', max: 200, optional: true, multiline: true },
  { key: 'company_description', label: 'Company Description', max: 800, multiline: true },
];

const statusConfig_de = {
  draft: { label: 'Entwurf', color: 'bg-yellow-500/20 text-yellow-500' },
  review: { label: 'Zur Review', color: 'bg-blue-500/20 text-blue-500' },
  approved: { label: 'Freigegeben', color: 'bg-green-500/20 text-green-500' },
  rejected: { label: 'Abgelehnt', color: 'bg-red-500/20 text-red-500' },
  video_generation: { label: 'Zur Generierung', color: 'bg-purple-500/20 text-purple-500' },
};
const statusConfig_en = {
  draft: { label: 'Draft', color: 'bg-yellow-500/20 text-yellow-500' },
  review: { label: 'In Review', color: 'bg-blue-500/20 text-blue-500' },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-500' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-500' },
  video_generation: { label: 'Generating', color: 'bg-purple-500/20 text-purple-500' },
};

const statusTabs_de = [
  { key: 'all', label: 'Alle' },
  { key: 'draft', label: 'Entwurf' },
  { key: 'review', label: 'Zur Review' },
  { key: 'approved', label: 'Freigegeben' },
  { key: 'rejected', label: 'Abgelehnt' },
  { key: 'video_generation', label: 'Zur Generierung' },
];
const statusTabs_en = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'review', label: 'In Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'video_generation', label: 'Generating' },
];

// (Demo-Content-Pieces werden vom Backend geladen — siehe useEffect unten.)

const ShowcaseProduktion = ({ sessionId, language, additionalContent = [] }) => {
  const { t, lang } = useLanguage();
  const isEn = lang === 'en';
  // ─── Showcase Demo-Stubs (ersetzen Auth + Subscription + Routing) ──────────
  // Diese Stubs erlauben es, das volle Produktion-UI ohne Backend zu rendern.
  // Alle Schreib-Operationen werden zu freundlichen Demo-Toasts.
  const getAuthHeader = () => ({});
  const subscription = { tier: 'pro', canUse: () => true };
  const searchParams = { get: () => null };
  const setSearchParams = () => {};
  // Mock axios: GET liefert leere Sammlung, alle Mutationen werden geblockt.
  const demoToast = isEn ? 'Not available in demo' : 'Nicht in Demo verfügbar';
  const axios = {
    get: async () => ({ data: { content_pieces: [], gallery: [], tags: [], folders: [], comments: [], versions: [] } }),
    post: async () => { toast.info(demoToast); throw new Error('demo'); },
    put: async () => { toast.info(demoToast); throw new Error('demo'); },
    delete: async () => { toast.info(demoToast); throw new Error('demo'); },
    patch: async () => { toast.info(demoToast); throw new Error('demo'); },
  };
  // ─────────────────────────────────────────────────────────────────────────

  const [showVideoGate, setShowVideoGate] = useState(false);
  
  const statusConfig = lang === 'de' ? statusConfig_de : statusConfig_en;
  const statusTabs = lang === 'de' ? statusTabs_de : statusTabs_en;
  const PRESS_RELEASE_FIELDS = lang === 'de' ? PRESS_RELEASE_FIELDS_DE : PRESS_RELEASE_FIELDS_EN;
  const [content, setContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedContent, setSelectedContent] = useState(null);
  const [activeTab, setActiveTab] = useState('text');
  const [versions, setVersions] = useState([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [blogTitle, setBlogTitle] = useState('');
  const [blogSubtitle, setBlogSubtitle] = useState('');
  const [editedMeta, setEditedMeta] = useState({});
  const [gallery, setGallery] = useState([]);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [additionalPlatforms, setAdditionalPlatforms] = useState([]);
  const [deletedContent, setDeletedContent] = useState(null);
  
  // Image selection state
  const [selectedImages, setSelectedImages] = useState([]);
  
  // Press release (Pressemitteilung) structured content state
  const [editedStructuredContent, setEditedStructuredContent] = useState({
    headline: '',
    subtitle: '',
    short_text: '',
    body_text: '',
    keywords: '',
    company_description: ''
  });
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isPostingComment, setIsPostingComment] = useState(false);
  
  // New state for scheduling flow
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [suggestedReason, setSuggestedReason] = useState('');
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showPlatformAdaptModal, setShowPlatformAdaptModal] = useState(false);
  const [isAdapting, setIsAdapting] = useState(false);
  const [scheduledContentId, setScheduledContentId] = useState(null);
  const [generatingImageId, setGeneratingImageId] = useState(null);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refineImageInstruction, setRefineImageInstruction] = useState('');
  const refineImageInputRef = useRef(null);
  const [editImageInstruction, setEditImageInstruction] = useState('');
  const [editingImageUrl, setEditingImageUrl] = useState(null);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const editImageInputRef = useRef(null);
  const [isRefining, setIsRefining] = useState(false);
  const [refineType, setRefineType] = useState('text');
  const [enabledPlatforms, setEnabledPlatforms] = useState(null);
  const [customChannels, setCustomChannels] = useState([]);
  const [platformCharLimits, setPlatformCharLimits] = useState({});
  
  // Conflict dialog state (duplicate schedule detection)
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Multi-select & bulk delete state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Expanded modal state
  const [showExpandedModal, setShowExpandedModal] = useState(false);

  // Tags & Folders state
  const [tags, setTags] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = all
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#00D9C8');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [showFolderMoveModal, setShowFolderMoveModal] = useState(false);
  const [movingContentId, setMovingContentId] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeletedIds, setBulkDeletedIds] = useState(null);
  const [undoCountdown, setUndoCountdown] = useState(0);

  // Filter platforms based on user's enabled platforms config
  const activePlatforms = (() => {
    if (enabledPlatforms === null || enabledPlatforms.length === 0) return platforms;
    const filtered = platforms.filter(p => enabledPlatforms.includes(p.key));
    customChannels.filter(c => enabledPlatforms.includes(c.id)).forEach(c => {
      const mainField = c.fields?.[0];
      filtered.push({
        key: c.id, label: c.name, icon: Layers, color: '#A0AEC0',
        limit: mainField?.maxLength || 500
      });
    });
    return filtered;
  })();

  // Get platform configuration
  const getPlatformConfig = (platform) => {
    if (PLATFORM_CONFIG[platform]) return PLATFORM_CONFIG[platform];
    // Check custom channels
    const cc = customChannels.find(c => c.id === platform);
    if (cc) {
      const mainField = cc.fields?.[0];
      return {
        name: cc.name, icon: Layers, color: '#A0AEC0',
        maxLength: mainField?.maxLength || 500, hasHeadline: cc.fields?.length > 1,
        headlineMax: cc.fields?.[1]?.maxLength || 100,
        hashtags: 0, imageLimit: 1, image: null
      };
    }
    return PLATFORM_CONFIG.linkedin;
  };

  // Get max images for current platform
  const getMaxImages = () => {
    if (!selectedContent) return 1;
    const config = getPlatformConfig(selectedContent.platform);
    return config.imageLimit || 1;
  };

  // Get character limit for current platform
  const getCharLimit = () => {
    if (!selectedContent) return 3000;
    const config = getPlatformConfig(selectedContent.platform);
    const userLimit = platformCharLimits[selectedContent.platform];
    if (userLimit && userLimit > 0) return userLimit;
    return config.maxLength || 3000;
  };

  // Toggle image selection
  const toggleImageSelection = (imageUrl) => {
    const maxImages = getMaxImages();
    
    if (selectedImages.includes(imageUrl)) {
      // Remove from selection
      setSelectedImages(prev => prev.filter(img => img !== imageUrl));
    } else {
      // Add to selection if under limit
      if (selectedImages.length < maxImages) {
        setSelectedImages(prev => [...prev, imageUrl]);
      } else {
        const config = getPlatformConfig(selectedContent.platform);
        toast.error(`Max. ${maxImages} image${maxImages !== 1 ? 's' : ''} for ${config.name}`);
      }
    }
  };

  // Lädt Demo-Content vom Backend (showcase_content.json) — sprachabhängig
  useEffect(() => {
    const loadPreseedContent = async () => {
      setIsLoading(true);
      try {
        const sid = sessionStorage.getItem(`showcase_session_${lang}`) || '';
        const response = await fetch(`/api/showcase/${lang}/preseed`, {
          headers: { 'X-Showcase-Session-Id': sid },
        });

        if (response.ok) {
          const data = await response.json();
          const posts = (data.preseed_posts || [])
            .filter(post => post.text && post.text.trim() !== '' && !post.text.startsWith('[DEIN') && !post.text.startsWith('[YOUR'))
            .map((post, idx) => ({
              id: post.id || `preseed-${idx}`,
              platform: post.platform,
              text: post.text,
              text_content: post.text,
              headline: post.text.startsWith('#') ? post.text.split('\n')[0].replace(/^#+\s*/, '') : null,
              title: post.text.startsWith('#')
                ? post.text.split('\n')[0].replace(/^#+\s*/, '')
                : (post.text.substring(0, 80).replace(/\n/g, ' ').trim() + (post.text.length > 80 ? '…' : '')),
              status: 'draft',
              created_at: new Date().toISOString(),
              content_number: 100 + idx,
              images: post.image_url && post.image_url !== 'https://...' ? [{ url: post.image_url }] : [],
              image_urls: post.image_url && post.image_url !== 'https://...' ? [post.image_url] : [],
              tags: [],
              day: post.day,
              type: 'preseed',
            }));

          setContent(posts);
        }
      } catch (err) {
        console.error('Failed to load preseed content:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreseedContent();
  }, [lang]);

  // Merge content forwarded from ShowcaseKreation (via "View in Production")
  // Newly forwarded pieces appear at the top, dedupes by id.
  useEffect(() => {
    if (!additionalContent || additionalContent.length === 0) return;
    setContent(prev => {
      const newIds = additionalContent.map(c => c.id);
      const filtered = prev.filter(c => !newIds.includes(c.id));
      return [...additionalContent, ...filtered];
    });
  }, [additionalContent]);

  useEffect(() => {
    fetchContent();
    fetchGallery();
    fetchTagsAndFolders();
    // Fetch platform config for filtering
    axios.get(`${API}/platform-config`, { headers: getAuthHeader() })
      .then(r => {
        setEnabledPlatforms(r.data.enabled_platforms || []);
        setCustomChannels(r.data.custom_channels || []);
        setPlatformCharLimits(r.data.platform_char_limits || {});
      })
      .catch(() => {});
  }, []);

  // Open content piece from URL parameter
  useEffect(() => {
    const contentId = searchParams.get('content');
    if (contentId && content.length > 0 && !selectedContent) {
      const contentPiece = content.find(c => c.id === contentId);
      if (contentPiece) {
        openDetailPanel(contentPiece);
        // Clear the URL parameter
        setSearchParams({});
      }
    }
  }, [content, searchParams]);

  useEffect(() => {
    filterContent();
  }, [content, searchQuery, statusFilter, platformFilter, activeFolder, activeTagFilter]);

  const fetchContent = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/content`, {
        headers: getAuthHeader()
      });
      setContent(response.data.content_pieces || []);
    } catch (err) {
      toast.error('Fehler beim Laden des Contents');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`, {
        headers: getAuthHeader()
      });
      setGallery(response.data.gallery || []);
    } catch (err) {
      console.error('Failed to fetch gallery:', err);
    }
  };

  const fetchTagsAndFolders = async () => {
    try {
      const [tagsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/tags`, { headers: getAuthHeader() }),
        axios.get(`${API}/folders`, { headers: getAuthHeader() })
      ]);
      setTags(tagsRes.data.tags || []);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      console.error('Failed to fetch tags/folders:', err);
    }
  };

  const filterContent = () => {
    let filtered = [...content];

    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      
      // Check if searching for a content number (e.g., "#5", "5", "#12")
      const numberMatch = query.match(/^#?(\d+)$/);
      
      filtered = filtered.filter(c => {
        // Search by content number
        if (numberMatch) {
          const searchNum = parseInt(numberMatch[1], 10);
          if (c.content_number === searchNum) return true;
        }
        
        // Search in title
        if (c.title?.toLowerCase().includes(query)) return true;
        
        // Search in text content
        if (c.text_content?.toLowerCase().includes(query)) return true;
        
        // Search in platform
        if (c.platform?.toLowerCase().includes(query)) return true;
        
        // Search in goal
        if (c.goal?.toLowerCase().includes(query)) return true;
        
        // Search in target audience
        if (c.target_audience?.toLowerCase().includes(query)) return true;
        
        // Search in SEO metadata (keywords, description)
        if (c.seo_metadata) {
          if (c.seo_metadata.keywords?.some(kw => kw.toLowerCase().includes(query))) return true;
          if (c.seo_metadata.meta_description?.toLowerCase().includes(query)) return true;
          if (c.seo_metadata.primary_keyword?.toLowerCase().includes(query)) return true;
        }
        
        // Search in structured content (Pressemitteilung)
        if (c.structured_content) {
          if (c.structured_content.headline?.toLowerCase().includes(query)) return true;
          if (c.structured_content.subheadline?.toLowerCase().includes(query)) return true;
          if (c.structured_content.lead?.toLowerCase().includes(query)) return true;
        }

        // Search in tags
        if (c.tags?.some(t => t.toLowerCase().includes(query))) return true;
        
        return false;
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (platformFilter) {
      filtered = filtered.filter(c => c.platform === platformFilter);
    }

    if (activeFolder) {
      filtered = filtered.filter(c => c.folder_id === activeFolder);
    }

    if (activeTagFilter) {
      filtered = filtered.filter(c => c.tags?.includes(activeTagFilter));
    }

    setFilteredContent(filtered);
  };

  const openDetailPanel = async (contentPiece) => {
    setSelectedContent(contentPiece);
    setEditedText(contentPiece.text_content || '');
    setBlogTitle(contentPiece.content_headline || '');
    setBlogSubtitle(contentPiece.content_subtitle || '');
    setEditedMeta({
      platform: contentPiece.platform,
      target_audience: contentPiece.target_audience || '',
      goal: contentPiece.goal || '',
      scheduled_at: contentPiece.scheduled_at || '',
      source_info: contentPiece.source_info || ''
    });
    setActiveTab('text');
    // Initialize selected images: prefer persisted selection, fall back to all images
    setSelectedImages(contentPiece.selected_image_urls ?? contentPiece.image_urls ?? []);
    
    // Initialize structured content for Pressemitteilung or Pinterest
    if (contentPiece.platform === 'pressemitteilung' && contentPiece.structured_content) {
      setEditedStructuredContent(contentPiece.structured_content);
    } else if (contentPiece.platform === 'pinterest') {
      if (contentPiece.structured_content && contentPiece.structured_content.pin_title) {
        setEditedStructuredContent(contentPiece.structured_content);
      } else {
        // Old content without structured pin_title — auto-generate via LLM
        const text = contentPiece.text_content || '';
        setEditedStructuredContent({
          pin_title: '',
          pin_description: text.slice(0, 500)
        });
        if (text) {
          axios.post(`${API}/content/${contentPiece.id}/generate-pin-title`, {}, {
            headers: getAuthHeader()
          }).then(res => {
            setEditedStructuredContent({
              pin_title: res.data.pin_title || '',
              pin_description: res.data.pin_description || text.slice(0, 500)
            });
            // Update content in list and selected
            setContent(prev => prev.map(c => 
              c.id === contentPiece.id ? { ...c, structured_content: res.data.structured_content } : c
            ));
            setSelectedContent(prev => prev ? { ...prev, structured_content: res.data.structured_content } : prev);
          }).catch(() => {});
        }
      }
    } else if (contentPiece.platform === 'youtube') {
      if (contentPiece.structured_content && contentPiece.structured_content.youtube_script) {
        setEditedStructuredContent(contentPiece.structured_content);
      } else {
        setEditedStructuredContent({
          youtube_title: contentPiece.content_headline || '',
          youtube_script: contentPiece.text_content || '',
          youtube_description: '',
          youtube_tags: ''
        });
      }
    } else if (contentPiece.platform?.startsWith('custom_')) {
      // Custom channel: populate from custom_fields or fall back to text_content
      const cf = contentPiece.custom_fields || {};
      const initFields = {};
      // Try to get field config from customChannels
      const ccDef = customChannels.find(c => c.id === contentPiece.platform);
      if (ccDef && ccDef.fields) {
        ccDef.fields.forEach((field, idx) => {
          const val = cf[field.name];
          if (val !== undefined && val !== null) {
            initFields[`custom_${field.name}`] = val;
          } else if (idx === 0 && !Object.keys(cf).length && contentPiece.text_content) {
            // Fallback: first field gets text_content when custom_fields is empty
            initFields[`custom_${field.name}`] = contentPiece.text_content;
          } else {
            initFields[`custom_${field.name}`] = '';
          }
        });
      } else {
        // No channel definition found — put text_content in a generic field
        initFields['custom_Text'] = contentPiece.text_content || '';
      }
      setEditedStructuredContent(initFields);
    } else {
      setEditedStructuredContent({
        headline: '',
        subtitle: '',
        short_text: '',
        body_text: '',
        keywords: '',
        company_description: ''
      });
    }

    // Fetch versions
    try {
      const response = await axios.get(`${API}/content/${contentPiece.id}/versions`, {
        headers: getAuthHeader()
      });
      setVersions(response.data.versions || []);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
    }
    
    // Fetch comments
    fetchComments(contentPiece.id);
  };

  const closeDetailPanel = () => {
    setSelectedContent(null);
    setVersions([]);
    setSelectedImages([]);
    setComments([]);
    setNewComment('');
    setEditedStructuredContent({
      headline: '',
      subtitle: '',
      short_text: '',
      body_text: '',
      keywords: '',
      company_description: ''
    });
  };

  // Comment functions
  const fetchComments = async (contentId) => {
    setIsLoadingComments(true);
    try {
      const response = await axios.get(`${API}/content/${contentId}/comments`, {
        headers: getAuthHeader()
      });
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handlePostComment = async () => {
    if (!selectedContent || !newComment.trim()) return;
    
    setIsPostingComment(true);
    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/comments`,
        { text: newComment.trim() },
        { headers: getAuthHeader() }
      );
      setComments(prev => [...prev, response.data.comment]);
      setNewComment('');
      toast.success(t.produktion.commentAdded);
    } catch (err) {
      toast.error(t.produktion.commentFailed);
    } finally {
      setIsPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedContent) return;
    
    try {
      await axios.delete(
        `${API}/content/${selectedContent.id}/comments/${commentId}`,
        { headers: getAuthHeader() }
      );
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success(t.produktion.commentDeleted);
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return t.produktion.justNow;
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Tag management functions
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await axios.post(`${API}/tags`, { name: newTagName.trim(), color: newTagColor }, { headers: getAuthHeader() });
      setTags(prev => [...prev, res.data.tag]);
      setNewTagName('');
      toast.success(t.produktion.tagCreated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await axios.delete(`${API}/tags/${tagId}`, { headers: getAuthHeader() });
      setTags(prev => prev.filter(t => t.id !== tagId));
      if (activeTagFilter) {
        const deleted = tags.find(t => t.id === tagId);
        if (deleted && activeTagFilter === deleted.name) setActiveTagFilter(null);
      }
      // Refresh content to reflect removed tags
      fetchContent();
      toast.success(t.produktion.tagDeleted);
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggleTag = async (contentId, tagName) => {
    const piece = content.find(c => c.id === contentId);
    if (!piece) return;
    const currentTags = piece.tags || [];
    const newTags = currentTags.includes(tagName) 
      ? currentTags.filter(t => t !== tagName) 
      : [...currentTags, tagName];
    try {
      const res = await axios.put(`${API}/content/${contentId}`, { tags: newTags }, { headers: getAuthHeader() });
      setContent(prev => prev.map(c => c.id === contentId ? res.data.content : c));
      if (selectedContent?.id === contentId) {
        setSelectedContent(res.data.content);
      }
    } catch (err) {
      toast.error('Tag-Fehler');
    }
  };

  // Folder management functions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await axios.post(`${API}/folders`, { name: newFolderName.trim() }, { headers: getAuthHeader() });
      setFolders(prev => [...prev, res.data.folder]);
      setNewFolderName('');
      setShowNewFolderInput(false);
      toast.success(t.produktion.folderCreated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleRenameFolder = async (folderId) => {
    if (!editingFolderName.trim()) return;
    try {
      const res = await axios.put(`${API}/folders/${folderId}`, { name: editingFolderName.trim() }, { headers: getAuthHeader() });
      setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: res.data.folder.name } : f));
      setEditingFolderId(null);
      toast.success(t.produktion.folderRenamed);
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/folders/${folderId}`, { headers: getAuthHeader() });
      setFolders(prev => prev.filter(f => f.id !== folderId));
      if (activeFolder === folderId) setActiveFolder(null);
      fetchContent();
      toast.success(t.produktion.folderDeleted);
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleMoveToFolder = async (contentId, folderId) => {
    try {
      const res = await axios.put(`${API}/content/${contentId}`, { folder_id: folderId || '' }, { headers: getAuthHeader() });
      setContent(prev => prev.map(c => c.id === contentId ? res.data.content : c));
      if (selectedContent?.id === contentId) setSelectedContent(res.data.content);
      fetchTagsAndFolders(); // refresh counts
      setShowFolderMoveModal(false);
      setMovingContentId(null);
      toast.success(folderId ? t.produktion.movedToFolder : t.produktion.removedFromFolder);
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleSaveText = async () => {
    if (!selectedContent) return;
    setIsSaving(true);

    try {
      const payload = { text_content: editedText };
      // Save blog title + subtitle
      if (selectedContent.platform === 'blog') {
        payload.content_headline = blogTitle;
        payload.content_subtitle = blogSubtitle;
      }
      const response = await axios.put(`${API}/content/${selectedContent.id}`, payload, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.produktion.changesSaved);

      // Refresh versions
      const versionsRes = await axios.get(`${API}/content/${selectedContent.id}/versions`, {
        headers: getAuthHeader()
      });
      setVersions(versionsRes.data.versions || []);
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Save custom channel fields
  const handleSaveCustomFields = async () => {
    if (!selectedContent) return;
    const cc = customChannels.find(c => c.id === selectedContent.platform);
    if (!cc) return;

    // Build custom_fields from editedStructuredContent
    const customFields = {};
    const textParts = [];
    for (const field of cc.fields) {
      const val = editedStructuredContent[`custom_${field.name}`] ?? selectedContent.custom_fields?.[field.name] ?? '';
      if (val.length > field.maxLength) {
        toast.error(`${field.name}: ${val.length}/${field.maxLength} Zeichen — zu lang!`);
        return;
      }
      customFields[field.name] = val;
      if (val) textParts.push(val);
    }

    setIsSaving(true);
    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        text_content: textParts.join('\n\n'),
        custom_fields: customFields
      }, { headers: getAuthHeader() });
      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.produktion.saved);
    } catch (err) {
      toast.error(t.produktion.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };


  // Save structured content for Pressemitteilung or Pinterest
  const handleSaveStructuredContent = async () => {
    if (!selectedContent) return;
    
    // Validate Pinterest limits
    if (selectedContent.platform === 'pinterest') {
      if ((editedStructuredContent.pin_title || '').length > 100) {
        toast.error('Pin-Titel darf max. 100 Zeichen haben');
        return;
      }
      if ((editedStructuredContent.pin_description || '').length > 500) {
        toast.error('Pin-Beschreibung darf max. 500 Zeichen haben');
        return;
      }
    }
    
    // Validate YouTube limits
    if (selectedContent.platform === 'youtube') {
      if ((editedStructuredContent.youtube_title || '').length > 100) {
        toast.error('Video-Titel darf max. 100 Zeichen haben');
        return;
      }
      if ((editedStructuredContent.youtube_script || '').length > 450) {
        toast.error('Skript darf max. 450 Zeichen haben');
        return;
      }
    }
    
    setIsSaving(true);

    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        structured_content: editedStructuredContent
      }, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(
        selectedContent.platform === 'pinterest' ? 'Pin gespeichert' : 
        selectedContent.platform === 'youtube' ? 'YouTube-Skript gespeichert' :
        'Pressemitteilung gespeichert'
      );
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateText = async () => {
    if (!selectedContent) return;
    setIsRegenerating(true);

    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/regenerate-text`, {}, {
        headers: getAuthHeader()
      });

      // Update text content
      setEditedText(response.data.text_content);
      setSelectedContent(prev => ({ 
        ...prev, 
        text_content: response.data.text_content,
        structured_content: response.data.structured_content || prev.structured_content
      }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, text_content: response.data.text_content } : c
      ));
      setVersions(prev => [response.data.version, ...prev]);
      
      // Update structured content if it's a press release, pinterest or youtube
      if ((selectedContent.platform === 'pressemitteilung' || selectedContent.platform === 'pinterest' || selectedContent.platform === 'youtube') && response.data.structured_content) {
        setEditedStructuredContent(response.data.structured_content);
      }
      
      toast.success(t.produktion.textRegenerated);
    } catch (err) {
      toast.error('Generierung fehlgeschlagen');
    } finally {
      setIsRegenerating(false);
    }
  };

  const [regeneratingField, setRegeneratingField] = useState(null);
  const handleRegenerateField = async (fieldKey, fieldLabel) => {
    if (!selectedContent) return;
    setRegeneratingField(fieldKey);
    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/regenerate-field`, {
        field_key: fieldKey,
        field_label: fieldLabel,
        current_content: editedStructuredContent
      }, { headers: getAuthHeader() });
      const newValue = response.data.field_value;
      setEditedStructuredContent(prev => ({ ...prev, [fieldKey]: newValue }));
      toast.success(`${fieldLabel} neu generiert`);
    } catch (err) {
      toast.error(`Fehler bei ${fieldLabel}`);
    } finally {
      setRegeneratingField(null);
    }
  };

  const handleRefine = async (type = 'text', customInstruction = null) => {
    const instruction = customInstruction || refineInstruction;
    if (!selectedContent || !instruction.trim()) return;
    setIsRefining(true);
    setRefineType(type);
    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/refine`, {
        instruction: instruction.trim(),
        type,
      }, { headers: getAuthHeader(), timeout: 60000 });

      if (response.data.type === 'text') {
        setEditedText(response.data.text_content);
        setSelectedContent(prev => ({ ...prev, text_content: response.data.text_content }));
        setContent(prev => prev.map(c =>
          c.id === selectedContent.id ? { ...c, text_content: response.data.text_content } : c
        ));
        if (response.data.version) {
          setVersions(prev => [response.data.version, ...prev]);
        }
        setRefineInstruction('');
        toast.success(t.produktion.textRefined);
      } else if (response.data.type === 'image') {
        setSelectedContent(prev => ({
          ...prev,
          image_urls: response.data.all_images || [...(prev.image_urls || []), response.data.image_url]
        }));
        setContent(prev => prev.map(c =>
          c.id === selectedContent.id
            ? { ...c, image_urls: response.data.all_images || [...(c.image_urls || []), response.data.image_url] }
            : c
        ));
        const maxImages = getMaxImages();
        if (selectedImages.length < maxImages) {
          setSelectedImages(prev => [...prev, response.data.image_url]);
        }
        setRefineImageInstruction('');
        toast.success(t.produktion.imageGenerated);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t.produktion.refinementFailed;
      toast.error(msg);
    } finally {
      setIsRefining(false);
    }
  };
  const handleEditImage = async () => {
    if (!selectedContent || !editImageInstruction.trim() || !editingImageUrl) return;
    setIsEditingImage(true);
    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/edit-image`,
        {
          image_url: editingImageUrl,
          instruction: editImageInstruction.trim(),
        },
        { headers: getAuthHeader(), timeout: 90000 }
      );
      setSelectedContent(prev => ({ ...prev, image_urls: response.data.all_images }));
      setContent(prev => prev.map(c =>
        c.id === selectedContent.id ? { ...c, image_urls: response.data.all_images } : c
      ));
      if (selectedImages.length < getMaxImages()) {
        setSelectedImages(prev => [...prev, response.data.new_image_url]);
      }
      setEditImageInstruction('');
      setEditingImageUrl(null);
      toast.success(lang === 'de' ? 'Bild bearbeitet!' : 'Image edited!');
    } catch (err) {
      const msg = err.response?.data?.detail || (lang === 'de' ? 'Bildbearbeitung fehlgeschlagen' : 'Image edit failed');
      toast.error(msg);
    } finally {
      setIsEditingImage(false);
    }
  };
  const handleSaveMeta = async () => {
    if (!selectedContent) return;
    setIsSaving(true);

    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, editedMeta, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success(t.produktion.changesSaved);
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateImage = async (contentId) => {
    setGeneratingImageId(contentId);
    try {
      const response = await axios.post(`${API}/content/${contentId}/generate-image`, {}, {
        headers: getAuthHeader(),
        timeout: 60000
      });

      if (response.data.success) {
        const newImageUrl = response.data.image_url;
        // Update content with new image
        setContent(prev => prev.map(c => 
          c.id === contentId 
            ? { ...c, image_urls: [...(c.image_urls || []), newImageUrl] } 
            : c
        ));
        if (selectedContent?.id === contentId) {
          setSelectedContent(prev => ({
            ...prev,
            image_urls: [...(prev.image_urls || []), newImageUrl]
          }));
          // Auto-select the new image if under limit
          const maxImages = getMaxImages();
          if (selectedImages.length < maxImages) {
            setSelectedImages(prev => [...prev, newImageUrl]);
          }
        }
        toast.success(t.produktion.imageRegenerated);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Bildgenerierung fehlgeschlagen';
      toast.error(errorMsg);
    } finally {
      setGeneratingImageId(null);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedContent) return;

    try {
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        status: newStatus
      }, {
        headers: getAuthHeader()
      });

      setSelectedContent(response.data.content);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? response.data.content : c));
      toast.success('Status aktualisiert');
    } catch (err) {
      toast.error('Status-Änderung fehlgeschlagen');
    }
  };

  // Submit YouTube content for video generation
  const handleSubmitForGeneration = async () => {
    if (!selectedContent || selectedContent.platform !== 'youtube') return;
    
    try {
      await axios.post(`${API}/content/${selectedContent.id}/submit-for-generation`, {}, {
        headers: getAuthHeader()
      });
      
      setSelectedContent(prev => ({ ...prev, status: 'video_generation' }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, status: 'video_generation' } : c
      ));
      toast.success('Skript zur Video-Generierung aufgegeben');
    } catch (err) {
      toast.error('Fehler beim Aufgeben zur Generierung');
    }
  };


  // New scheduling flow functions
  const handleApprove = async () => {
    if (!selectedContent) return;
    
    const platformName = platforms.find(p => p.key === selectedContent.platform)?.label || selectedContent.platform;
    
    // Validate image count
    const maxImages = getMaxImages();
    if (selectedImages.length > maxImages) {
      const bildText = lang === 'de' ? (maxImages === 1 ? 'Bild' : 'Bilder') : (maxImages === 1 ? 'image' : 'images');
      toast.error(`Auf ${platformName} kann man nur max. ${maxImages} ${bildText} pro Beitrag posten. Bitte passe deine Auswahl entsprechend an.`);
      return;
    }
    
    // Validate character count - special handling for Pressemitteilung and Pinterest
    if (selectedContent.platform === 'pressemitteilung') {
      // Validate each structured field — HTML-aware: strip tags before counting
      // because fields are stored as HTML (<p>, <strong>, …) but the character
      // limit is on the *visible* text (same as the live counter in
      // RichTextFieldEditor).
      for (const field of PRESS_RELEASE_FIELDS) {
        const rawValue = editedStructuredContent[field.key] || '';
        const visibleValue = richFieldHtmlToText(rawValue);
        if (visibleValue.length > field.max) {
          toast.error(`Das Feld "${field.label}" überschreitet das Limit von ${field.max} Zeichen (aktuell: ${visibleValue.length}). Bitte kürzen.`);
          return;
        }
        // Check required fields (non-optional)
        if (!field.optional && !visibleValue.trim()) {
          toast.error(`Das Feld "${field.label}" darf nicht leer sein.`);
          return;
        }
      }
    } else if (selectedContent.platform === 'pinterest') {
      const pinTitle = editedStructuredContent.pin_title || '';
      const pinDesc = editedStructuredContent.pin_description || '';
      if (pinTitle.length > 100) {
        toast.error(`Pin-Titel überschreitet das Limit von 100 Zeichen (aktuell: ${pinTitle.length}). Bitte kürzen.`);
        return;
      }
      if (!pinTitle.trim()) {
        toast.error('Pin-Titel darf nicht leer sein.');
        return;
      }
      if (pinDesc.length > 500) {
        toast.error(`Pin-Beschreibung überschreitet das Limit von 500 Zeichen (aktuell: ${pinDesc.length}). Bitte kürzen.`);
        return;
      }
    } else {
      // Standard character validation for other platforms
      const textContent = editedText || selectedContent.text_content || '';
      const charCount = textContent.length;
      const maxChars = getCharLimit();
      const config = getPlatformConfig(selectedContent.platform);
      
      if (charCount > maxChars) {
        toast.error(`Auf ${config.name} kann man nur max. ${maxChars.toLocaleString()} Zeichen pro Beitrag posten. Dein Text hat ${charCount.toLocaleString()} Zeichen. Bitte kürze deinen Text entsprechend.`);
        return;
      }
    }
    
    setIsLoadingSuggestion(true);
    setShowScheduleModal(true);
    
    try {
      const response = await axios.get(`${API}/content/${selectedContent.id}/suggest-schedule`, {
        headers: getAuthHeader()
      });
      
      const suggestedDate = new Date(response.data.suggested_datetime);
      setScheduleDate(suggestedDate.toISOString().split('T')[0]);
      setScheduleTime(suggestedDate.toTimeString().slice(0, 5));
      setSuggestedReason(response.data.reason);
    } catch (err) {
      // Fallback to tomorrow at 10:00
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().split('T')[0]);
      setScheduleTime('10:00');
      setSuggestedReason('Vorgeschlagene Zeit für optimale Reichweite.');
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  const handleScheduleContent = async (replaceEventId = null) => {
    if (!selectedContent || !scheduleDate || !scheduleTime) return;
    
    setIsScheduling(true);
    
    try {
      const scheduledAt = `${scheduleDate}T${scheduleTime}:00`;
      
      const payload = {
        scheduled_at: scheduledAt,
        selected_images: selectedImages
      };
      if (replaceEventId) {
        payload.replace_event_id = replaceEventId;
      }
      
      const response = await axios.post(`${API}/content/${selectedContent.id}/schedule`, payload, {
        headers: getAuthHeader()
      });
      
      // Delete all comments after approval (they are not needed anymore)
      if (comments.length > 0) {
        try {
          await axios.delete(`${API}/content/${selectedContent.id}/comments/all`, {
            headers: getAuthHeader()
          });
          setComments([]);
        } catch (err) {
          console.log('Failed to delete comments:', err);
        }
      }
      
      // Update local state
      setSelectedContent(prev => ({ ...prev, status: 'approved', scheduled_at: scheduledAt, image_urls: selectedImages }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, status: 'approved', scheduled_at: scheduledAt, image_urls: selectedImages } : c
      ));
      
      setScheduledContentId(selectedContent.id);
      setShowScheduleModal(false);
      setShowConflictDialog(false);
      setConflictData(null);
      
      toast.success(t.produktion.scheduledInCalendar);
      
      // Show platform adaptation dialog
      setShowPlatformAdaptModal(true);
      setAdditionalPlatforms([]);
      
    } catch (err) {
      console.error('Schedule error:', err.response?.status, err.response?.data);
      const status = err.response?.status;
      const data = err.response?.data;
      // Handle conflict (409) - detail can be object or string
      const detail = data?.detail;
      const isConflict = status === 409 && (
        (typeof detail === 'object' && detail?.conflict) ||
        (typeof detail === 'string' && detail.includes('bereits'))
      );
      
      if (isConflict) {
        const conflictInfo = typeof detail === 'object' ? detail : {};
        setConflictData({
          existingEventId: conflictInfo.existing_event_id || '',
          existingScheduledAt: conflictInfo.existing_scheduled_at || '',
          message: conflictInfo.message || detail || (lang === 'de' 
            ? 'Dieser Beitrag ist bereits für diesen Tag auf dieser Plattform eingeplant.' 
            : 'This post is already scheduled for this day on this platform.')
        });
        setShowScheduleModal(false);
        setShowConflictDialog(true);
      } else {
        toast.error(data?.detail?.message || data?.detail || t.produktion.schedulingFailed);
      }
    } finally {
      setIsScheduling(false);
    }
  };

  const handleConflictReplace = () => {
    if (conflictData?.existingEventId) {
      handleScheduleContent(conflictData.existingEventId);
    }
  };

  const handleConflictSaveDraft = async () => {
    if (!selectedContent) return;
    try {
      await axios.put(`${API}/content/${selectedContent.id}`, {
        status: 'draft'
      }, { headers: getAuthHeader() });
      setSelectedContent(prev => ({ ...prev, status: 'draft' }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, status: 'draft' } : c
      ));
      setShowConflictDialog(false);
      setConflictData(null);
      toast.success(t.produktion.savedAsDraft);
    } catch (err) {
      toast.error(t.produktion.saveFailed);
    }
  };

  // Multi-select helpers
  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredContent.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContent.map(c => c.id)));
    }
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setShowBulkDeleteConfirm(false);

    try {
      await axios.post(`${API}/content/bulk-delete`, { ids }, { headers: getAuthHeader() });
      setBulkDeletedIds(ids);
      setContent(prev => prev.filter(c => !ids.includes(c.id)));
      exitSelectMode();
      if (selectedContent && ids.includes(selectedContent.id)) closeDetailPanel();

      // Start 10s undo countdown
      setUndoCountdown(10);
      const interval = setInterval(() => {
        setUndoCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setBulkDeletedIds(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(t.produktion.deleteFailed);
    }
  };

  const handleBulkUndo = async () => {
    if (!bulkDeletedIds) return;
    try {
      await axios.post(`${API}/content/bulk-restore`, { ids: bulkDeletedIds }, { headers: getAuthHeader() });
      // Refetch content after restore
      const res = await axios.get(`${API}/content`, { headers: getAuthHeader() });
      const items = res.data.content_pieces || res.data.items || res.data || [];
      setContent(Array.isArray(items) ? items : []);
      toast.success(t.produktion.restored);
    } catch (err) {
      toast.error(t.produktion.restoreFailed);
    } finally {
      setBulkDeletedIds(null);
      setUndoCountdown(0);
    }
  };

  const handleAdaptPlatforms = async () => {
    console.log('handleAdaptPlatforms called:', { scheduledContentId, additionalPlatforms });
    
    if (!scheduledContentId || additionalPlatforms.length === 0) {
      console.log('Early return - missing data');
      setShowPlatformAdaptModal(false);
      return;
    }
    
    setIsAdapting(true);
    
    try {
      // Longer timeout for multiple platform adaptations (3 minutes)
      const response = await axios.post(`${API}/content/${scheduledContentId}/adapt-platforms`, {
        additional_platforms: additionalPlatforms
      }, {
        headers: getAuthHeader(),
        timeout: 180000 // 3 minutes for AI content generation
      });
      
      console.log('Adapt response:', response.data);
      
      if (response.data.new_pieces?.length > 0) {
        const newPieces = response.data.new_pieces;
        setContent(prev => [...prev, ...newPieces]);
        toast.success(lang === 'de' 
          ? `${newPieces.length} neue${newPieces.length === 1 ? 'r Entwurf' : ' Entwürfe'} erstellt`
          : `${newPieces.length} new draft${newPieces.length > 1 ? 's' : ''} created`
        );
        setShowPlatformAdaptModal(false);
        setScheduledContentId(null);
        // Automatically open the first new piece in the detail panel
        openDetailPanel(newPieces[0]);
      } else if (response.data.success) {
        // Success but no new pieces (e.g., all platforms were same as original)
        toast.info('Keine neuen Entwürfe erstellt (Plattformen bereits vorhanden)');
        setShowPlatformAdaptModal(false);
        setScheduledContentId(null);
        closeDetailPanel();
      }
    } catch (err) {
      console.error('Adapt error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Anpassung fehlgeschlagen';
      
      // Check if it was a timeout
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Die Generierung dauert zu lange. Bitte prüfe in einigen Minuten ob der Content erstellt wurde.');
      } else {
        toast.error(`Anpassung fehlgeschlagen: ${errorMessage}`);
      }
    } finally {
      setIsAdapting(false);
    }
  };

  const skipPlatformAdaption = () => {
    setShowPlatformAdaptModal(false);
    setScheduledContentId(null);
    closeDetailPanel();
  };

  // Legacy function kept for backwards compatibility
  const confirmApprove = async () => {
    if (!selectedContent) return;

    try {
      const response = await axios.post(`${API}/content/${selectedContent.id}/approve`, {
        additional_platforms: additionalPlatforms
      }, {
        headers: getAuthHeader()
      });

      setSelectedContent(prev => ({ ...prev, status: 'approved' }));
      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, status: 'approved' } : c));

      if (response.data.new_pieces?.length > 0) {
        setContent(prev => [...prev, ...response.data.new_pieces]);
        toast.success(`Freigegeben + ${response.data.new_pieces.length} neue Entwürfe erstellt`);
      } else {
        toast.success('Freigegeben und im Kalender gespeichert');
      }

      setShowApproveDialog(false);
    } catch (err) {
      toast.error('Freigabe fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    if (!selectedContent) return;

    try {
      await axios.delete(`${API}/content/${selectedContent.id}`, {
        headers: getAuthHeader()
      });

      setDeletedContent(selectedContent);
      setContent(prev => prev.filter(c => c.id !== selectedContent.id));
      closeDetailPanel();

      toast.success(
        <div className="flex items-center gap-2">
          <span>Gelöscht</span>
          <button 
            onClick={handleUndoDelete}
            className="text-[#00D9C8] hover:underline ml-2"
          >
            Rückgängig
          </button>
        </div>,
        { duration: 5000 }
      );

      setTimeout(() => setDeletedContent(null), 5000);
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleUndoDelete = async () => {
    if (!deletedContent) return;

    try {
      await axios.post(`${API}/content/${deletedContent.id}/restore`, {}, {
        headers: getAuthHeader()
      });

      setContent(prev => [...prev, { ...deletedContent, status: 'draft' }]);
      setDeletedContent(null);
      toast.success(t.produktion.contentRestored);
    } catch (err) {
      toast.error('Wiederherstellung fehlgeschlagen');
    }
  };

  const handleSaveImages = async () => {
    if (!selectedContent) return;
    setIsSaving(true);
    try {
      // Persist ONLY the selected subset: unchecked images are removed from the content piece.
      // This makes "save" match the user's visible selection, so the preview thumbnail
      // and the image grid reflect exactly what was selected after refresh.
      const newImageUrls = selectedImages;
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        image_urls: newImageUrls
      }, { headers: getAuthHeader() });
      const updated = response.data.content;
      setSelectedContent(updated);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? updated : c));
      // Keep selection in sync with the persisted list
      setSelectedImages(updated.image_urls || []);
      toast.success(t.produktion.imagesSaved);
    } catch (err) {
      toast.error(t.produktion.saveFailed);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!selectedContent) return;
    // Double confirmation before destructive action
    const firstMsg = lang === 'de'
      ? 'Möchtest du dieses Bild wirklich aus diesem Beitrag entfernen?'
      : 'Do you really want to remove this image from this post?';
    if (!window.confirm(firstMsg)) return;
    const secondMsg = lang === 'de'
      ? 'Dieser Vorgang kann nicht rückgängig gemacht werden. Wirklich endgültig löschen?'
      : 'This action cannot be undone. Really delete permanently?';
    if (!window.confirm(secondMsg)) return;

    try {
      const newImageUrls = (selectedContent.image_urls || []).filter(u => u !== imageUrl);
      const newSelected = (selectedContent.selected_image_urls ?? selectedContent.image_urls ?? []).filter(u => u !== imageUrl);
      const response = await axios.put(`${API}/content/${selectedContent.id}`, {
        image_urls: newImageUrls,
        selected_image_urls: newSelected
      }, { headers: getAuthHeader() });
      const updated = response.data.content;
      setSelectedContent(updated);
      setContent(prev => prev.map(c => c.id === selectedContent.id ? updated : c));
      setSelectedImages(prev => prev.filter(u => u !== imageUrl));
      toast.success(lang === 'de' ? 'Bild gelöscht' : 'Image deleted');
    } catch (err) {
      toast.error(lang === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedContent) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/upload-image`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSelectedContent(prev => ({ ...prev, image_urls: response.data.image_urls }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, image_urls: response.data.image_urls } : c
      ));
      toast.success(lang === 'de' ? 'Bild hochgeladen' : 'Image uploaded');
      fetchGallery();
    } catch (err) {
      toast.error('Upload fehlgeschlagen');
    }
  };

  const handleAttachGalleryImage = async (imageUrl) => {
    if (!selectedContent) return;

    const formData = new FormData();
    formData.append('image_url', imageUrl);

    try {
      const response = await axios.post(
        `${API}/content/${selectedContent.id}/attach-gallery-image`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Also update selectedImages so the newly attached gallery image is
      // persisted on the next schedule/save (selected_image_urls in DB).
      const newImgs = response.data.image_urls;
      setSelectedContent(prev => ({ ...prev, image_urls: newImgs, selected_image_urls: newImgs }));
      setContent(prev => prev.map(c => 
        c.id === selectedContent.id ? { ...c, image_urls: newImgs, selected_image_urls: newImgs } : c
      ));
      setSelectedImages(newImgs);
      setShowGalleryModal(false);
      toast.success(lang === 'de' ? 'Bild hinzugefügt' : 'Image added');
    } catch (err) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleShareEmail = () => {
    if (!selectedContent) return;
    
    const contentNumber = selectedContent.content_number || '—';
    const subject = `Content Piece #${contentNumber} - generiert mit xchannels.ai`;
    
    // Build formatted body
    let bodyText = '';
    
    // For Pressemitteilung, use structured content
    if (selectedContent.platform === 'pressemitteilung' && selectedContent.structured_content) {
      const sc = selectedContent.structured_content;
      if (sc.headline) bodyText += `${sc.headline}\n\n`;
      if (sc.subtitle) bodyText += `${sc.subtitle}\n\n`;
      if (sc.short_text) bodyText += `${sc.short_text}\n\n`;
      if (sc.body_text) bodyText += `${sc.body_text}\n\n`;
      if (sc.keywords) bodyText += `Schlüsselwörter: ${sc.keywords}\n\n`;
      if (sc.company_description) bodyText += `Über uns: ${sc.company_description}\n\n`;
    } else if (selectedContent.platform === 'pinterest') {
      const sc = selectedContent.structured_content || {};
      const title = sc.pin_title || '';
      const desc = sc.pin_description || selectedContent.text_content || '';
      if (title) bodyText += `${title}\n\n`;
      bodyText += desc;
    } else {
      bodyText = selectedContent.text_content || '';
    }
    
    // Add image URL if available
    const imageUrl = selectedImages.length > 0 ? selectedImages[0] : (selectedContent.image_urls?.[0] || '');
    if (imageUrl) {
      bodyText += `\n\n---\nBild: ${imageUrl}\n---`;
    }
    
    // Add footer
    bodyText += `\n\n\nGeneriert mit xchannels.ai\nhttps://xchannels.ai/`;
    
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(bodyText);
    
    window.location.href = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
  };

  const loadVersion = (version) => {
    setEditedText(version.text_content);
    toast.success(`Version ${version.version_number} geladen`);
  };

  const getPlatformIcon = (platformKey, size = 'w-4 h-4') => {
    const platform = platforms.find(p => p.key === platformKey);
    if (platform) {
      const Icon = platform.icon;
      return <Icon className={size} style={{ color: platform.color }} />;
    }
    // Check custom channels
    const cc = customChannels.find(c => c.id === platformKey);
    if (cc) {
      return <Layers className={size} style={{ color: '#A0AEC0' }} />;
    }
    return null;
  };

  const getPlatformLimit = (platformKey) => {
    // Check user's custom char limit first
    const userLimit = platformCharLimits[platformKey];
    if (userLimit && userLimit > 0) return userLimit;
    const platform = platforms.find(p => p.key === platformKey);
    if (platform) return platform?.limit || 3000;
    // Check custom channels
    const cc = customChannels.find(c => c.id === platformKey);
    if (cc) return cc.fields?.[0]?.maxLength || 500;
    return 3000;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="flex" data-testid="produktion-page">
      {/* Main Content - Hidden on mobile when detail panel is open */}
      <div className={`flex-1 transition-all ${selectedContent ? 'hidden lg:block lg:mr-[480px]' : ''}`}>
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-1 lg:mb-2">
            {t.produktion.title}
          </h1>
          <p className="text-sm lg:text-base text-[#A0AEC0]">
            {t.produktion.subtitle}
          </p>
        </div>

        {/* Top Bar - Responsive */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4 mb-4 lg:mb-6">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A6C7D]" />
            <Input
              placeholder={t.produktion.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#5A6C7D] text-sm"
              data-testid="search-input"
            />
          </div>

          {/* Filter & View Toggle Row */}
          <div className="flex items-center gap-2">
            {/* Platform Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0] text-xs lg:text-sm h-9 lg:h-10 px-2 lg:px-3">
                  <Filter className="w-4 h-4 lg:mr-2" />
                  <span className="hidden lg:inline">{platformFilter ? platforms.find(p => p.key === platformFilter)?.label : t.produktion.allPlatforms}</span>
                  <ChevronDown className="w-4 h-4 ml-1 lg:ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                <DropdownMenuItem 
                  onClick={() => setPlatformFilter(null)}
                  className="text-white hover:bg-[#0A1628]"
                >
                  {t.produktion.allPlatforms}
                </DropdownMenuItem>
                {platforms.map(platform => (
                  <DropdownMenuItem 
                    key={platform.key}
                    onClick={() => setPlatformFilter(platform.key)}
                    className="text-white hover:bg-[#0A1628]"
                  >
                    {getPlatformIcon(platform.key)}
                    <span className="ml-2">{platform.label}</span>
                  </DropdownMenuItem>
                ))}
                {customChannels.map(c => (
                  <DropdownMenuItem 
                    key={c.id}
                    onClick={() => setPlatformFilter(c.id)}
                    className="text-white hover:bg-[#0A1628]"
                  >
                    <Layers className="w-4 h-4" style={{ color: '#A0AEC0' }} />
                    <span className="ml-2">{c.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* View Toggle */}
            <div className="flex gap-1 bg-[#1A2B42] p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 lg:p-2 ${viewMode === 'grid' ? 'bg-[#00D9C8] text-[#0A1628]' : 'text-[#A0AEC0]'}`}
                data-testid="grid-view-btn"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 lg:p-2 ${viewMode === 'list' ? 'bg-[#00D9C8] text-[#0A1628]' : 'text-[#A0AEC0]'}`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Select Mode Toggle */}
            <Button
              variant={isSelectMode ? 'default' : 'outline'}
              onClick={() => isSelectMode ? exitSelectMode() : setIsSelectMode(true)}
              className={isSelectMode 
                ? 'bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] text-xs lg:text-sm h-9 lg:h-10'
                : 'border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0] text-xs lg:text-sm h-9 lg:h-10'
              }
              data-testid="select-mode-btn"
            >
              <Check className="w-4 h-4 lg:mr-1" />
              <span className="hidden lg:inline">{t.produktion.select}</span>
            </Button>
          </div>
        </div>

        {/* Status Tabs - Scrollable on mobile */}
        <div className="flex gap-1 mb-4 lg:mb-6 border-b border-[#1A2B42] overflow-x-auto pb-px">
          {statusTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 lg:px-4 py-2 text-xs lg:text-sm transition-colors whitespace-nowrap ${
                statusFilter === tab.key
                  ? 'text-[#00D9C8] border-b-2 border-[#00D9C8]'
                  : 'text-[#A0AEC0] hover:text-white'
              }`}
              data-testid={`status-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Folders & Tags Bar */}
        <div className="flex flex-col gap-3 mb-4">
          {/* Folders Row */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="folders-bar">
            <button
              onClick={() => setActiveFolder(null)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                !activeFolder ? 'bg-[#00D9C8]/20 text-[#00D9C8] border border-[#00D9C8]/40' : 'bg-[#1A2B42] text-[#A0AEC0] hover:text-white border border-transparent'
              }`}
              data-testid="folder-all-btn"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              {lang === 'de' ? 'Alle' : 'All'}
            </button>
            {folders.map(folder => (
              <div key={folder.id} className="relative group flex items-center" data-testid={`folder-${folder.id}`}>
                {editingFolderId === folder.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleRenameFolder(folder.id); }} className="flex items-center gap-1">
                    <Input
                      value={editingFolderName}
                      onChange={(e) => setEditingFolderName(e.target.value)}
                      className="h-7 w-28 text-xs bg-[#0A1628] border-[#00D9C8] text-white"
                      autoFocus
                      onBlur={() => { if (editingFolderName.trim()) handleRenameFolder(folder.id); else setEditingFolderId(null); }}
                      data-testid={`folder-rename-input-${folder.id}`}
                    />
                  </form>
                ) : (
                  <button
                    onClick={() => setActiveFolder(activeFolder === folder.id ? null : folder.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                      activeFolder === folder.id ? 'bg-[#00D9C8]/20 text-[#00D9C8] border border-[#00D9C8]/40' : 'bg-[#1A2B42] text-[#A0AEC0] hover:text-white border border-transparent'
                    }`}
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                    {folder.name}
                    <span className="text-[10px] opacity-60">({folder.content_count})</span>
                  </button>
                )}
                {editingFolderId !== folder.id && (
                  <div className="hidden group-hover:flex items-center ml-1 gap-0.5">
                    <button onClick={() => { setEditingFolderId(folder.id); setEditingFolderName(folder.name); }} className="p-0.5 text-[#5A6C7D] hover:text-white" data-testid={`folder-edit-${folder.id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDeleteFolder(folder.id)} className="p-0.5 text-[#5A6C7D] hover:text-red-400" data-testid={`folder-delete-${folder.id}`}>
                      <Trash className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {showNewFolderInput ? (
              <form onSubmit={(e) => { e.preventDefault(); handleCreateFolder(); }} className="flex items-center gap-1">
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder={t.produktion.folderName}
                  className="h-7 w-32 text-xs bg-[#0A1628] border-[#1A2B42] text-white placeholder:text-[#5A6C7D]"
                  autoFocus
                  onBlur={() => { if (!newFolderName.trim()) setShowNewFolderInput(false); }}
                  data-testid="new-folder-input"
                />
                <Button type="submit" size="sm" className="h-7 px-2 bg-[#00D9C8] text-[#0A1628]" data-testid="new-folder-submit">
                  <Check className="w-3 h-3" />
                </Button>
              </form>
            ) : (
              <button
                onClick={() => setShowNewFolderInput(true)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-[#5A6C7D] hover:text-[#00D9C8] hover:bg-[#1A2B42] transition-colors"
                data-testid="add-folder-btn"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Tags Row */}
          {tags.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="tags-bar">
              <Tag className="w-3.5 h-3.5 text-[#5A6C7D] flex-shrink-0" />
              {tags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => setActiveTagFilter(activeTagFilter === tag.name ? null : tag.name)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${
                    activeTagFilter === tag.name 
                      ? 'border-current opacity-100' 
                      : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                  style={{ color: tag.color, backgroundColor: `${tag.color}15` }}
                  data-testid={`tag-filter-${tag.id}`}
                >
                  {tag.name}
                </button>
              ))}
              <button
                onClick={() => setShowTagManager(true)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-[#5A6C7D] hover:text-[#00D9C8] hover:bg-[#1A2B42] transition-colors"
                data-testid="manage-tags-btn"
              >
                <Plus className="w-3 h-3" />
              </button>
              {activeTagFilter && (
                <button onClick={() => setActiveTagFilter(null)} className="text-xs text-[#5A6C7D] hover:text-white ml-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          {tags.length === 0 && (
            <button
              onClick={() => setShowTagManager(true)}
              className="flex items-center gap-1.5 text-xs text-[#5A6C7D] hover:text-[#00D9C8] transition-colors"
              data-testid="create-first-tag-btn"
            >
              <Tag className="w-3.5 h-3.5" />
              {lang === 'de' ? 'Tags erstellen' : 'Create tags'}
            </button>
          )}
        </div>

        {/* Selection Bar - shown when items are selected */}
        {isSelectMode && (
          <div className="flex items-center justify-between bg-[#1A2B42] border border-[#00D9C8]/30 p-3 mb-4 rounded" data-testid="selection-bar">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="w-5 h-5 border-2 border-[#00D9C8] rounded flex items-center justify-center transition-colors hover:bg-[#00D9C8]/20"
                data-testid="select-all-checkbox"
              >
                {selectedIds.size === filteredContent.length && filteredContent.length > 0 && (
                  <Check className="w-3 h-3 text-[#00D9C8]" />
                )}
                {selectedIds.size > 0 && selectedIds.size < filteredContent.length && (
                  <div className="w-2 h-2 bg-[#00D9C8] rounded-sm" />
                )}
              </button>
              <span className="text-sm text-white">
                {selectedIds.size > 0 
                  ? `${selectedIds.size} ${t.common.selected}`
                  : t.common.selectAll}
              </span>
            </div>
            {selectedIds.size > 0 && (
              <Button
                onClick={() => setShowBulkDeleteConfirm(true)}
                variant="destructive"
                size="sm"
                className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                data-testid="bulk-delete-btn"
              >
                <Trash className="w-4 h-4 mr-1" />
                {t.common.delete} ({selectedIds.size})
              </Button>
            )}
          </div>
        )}

        {/* Undo Bar - 10s countdown after deletion */}
        {bulkDeletedIds && undoCountdown > 0 && (
          <div className="flex items-center justify-between bg-[#1A2B42] border border-amber-500/30 p-3 mb-4 rounded animate-in fade-in" data-testid="undo-bar">
            <span className="text-sm text-[#A0AEC0]">
              {bulkDeletedIds.length} {t.produktion.postsDeleted} ({undoCountdown}s)
            </span>
            <Button
              onClick={handleBulkUndo}
              variant="outline"
              size="sm"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              data-testid="undo-delete-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {t.common.undo}
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#00D9C8]" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredContent.length === 0 && (
          <div className="text-center py-12">
            <FileEdit className="w-12 h-12 mx-auto mb-4 text-[#5A6C7D]" />
            <h3 className="text-lg font-bold text-white mb-2">{t.produktion.noContent}</h3>
            <p className="text-[#A0AEC0]">
              {searchQuery || statusFilter !== 'all' || platformFilter
                ? t.produktion.tryDifferentFilters
                : t.produktion.noContentDesc}
            </p>
          </div>
        )}

        {/* Grid View - 2 columns on mobile */}
        {!isLoading && viewMode === 'grid' && filteredContent.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
            {filteredContent.map((item, index) => {
              // Extract first two sentences for preview
              const getPreviewText = (text) => {
                if (!text) return '';
                const sentences = text.split(/[.!?]+/).filter(s => s.trim());
                return sentences.slice(0, 2).join('. ').trim() + (sentences.length > 0 ? '.' : '');
              };
              const previewText = getPreviewText(item.text_content);
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Card 
                    className={`bg-[#1A2B42] border-transparent hover:border-[#00D9C8] transition-colors cursor-pointer group h-full ${
                      isSelectMode && selectedIds.has(item.id) ? 'border-[#00D9C8] ring-1 ring-[#00D9C8]/50' : ''
                    }`}
                    onClick={() => isSelectMode ? toggleSelectId(item.id) : openDetailPanel(item)}
                    data-testid={`content-card-${item.id}`}
                  >
                    <div className="aspect-video bg-[#0A1628] relative overflow-hidden">
                      {/* Select checkbox overlay */}
                      {isSelectMode && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                            selectedIds.has(item.id) 
                              ? 'bg-[#00D9C8] border-[#00D9C8]' 
                              : 'border-white/60 bg-[#0A1628]/60'
                          }`} data-testid={`select-checkbox-${item.id}`}>
                            {selectedIds.has(item.id) && <Check className="w-3 h-3 text-[#0A1628]" />}
                          </div>
                        </div>
                      )}
                      {item.image_urls?.length > 0 ? (
                        <img 
                          src={(item.selected_image_urls && item.selected_image_urls.length > 0) ? item.selected_image_urls[0] : item.image_urls[0]} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          {getPlatformIcon(item.platform, 'w-10 h-10')}
                          {item.image_prompt && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="mt-2 text-xs text-[#00D9C8] hover:bg-[#00D9C8]/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerateImage(item.id);
                              }}
                              disabled={generatingImageId === item.id}
                              data-testid={`generate-image-card-${item.id}`}
                            >
                              {generatingImageId === item.id ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <ImageIcon className="w-3 h-3 mr-1" />
                              )}
                              {generatingImageId === item.id ? t.produktion.imageLoading : t.produktion.generateImage}
                            </Button>
                          )}
                        </div>
                      )}
                      {/* Content Number Badge */}
                      <div className="absolute top-2 right-2">
                        <div className="bg-[#0A1628]/90 px-2 py-1 rounded text-xs font-mono text-[#00D9C8]" data-testid={`content-number-${item.id}`}>
                          #{item.content_number || '—'}
                        </div>
                      </div>
                      <div className="absolute top-2 left-2">
                        <div className="bg-[#0A1628]/80 p-1.5">
                          {getPlatformIcon(item.platform)}
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-2 lg:p-4">
                      {(() => {
                        const pConfig = PLATFORM_CONFIG[item.platform];
                        const showHeadline = pConfig ? pConfig.hasHeadline : item.platform?.startsWith('custom_');
                        if (showHeadline) {
                          return (
                            <>
                              <h3 className="text-xs lg:text-sm font-medium text-white mb-1 lg:mb-2 leading-tight line-clamp-2">
                                {item.content_headline || item.title}
                              </h3>
                              {previewText && (
                                <p className="hidden lg:block text-xs text-[#A0AEC0] mb-3 line-clamp-3 leading-relaxed">
                                  {previewText}
                                </p>
                              )}
                            </>
                          );
                        }
                        return (
                          <p className="text-xs lg:text-sm text-[#A0AEC0] mb-3 line-clamp-3 leading-relaxed">
                            {previewText || item.text_content?.substring(0, 120)}
                          </p>
                        );
                      })()}
                      
                      <Badge className={statusConfig[item.status]?.color || 'bg-gray-500/20 text-gray-500'}>
                        {statusConfig[item.status]?.label || item.status}
                      </Badge>
                      {item.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {item.tags.slice(0, 3).map(tagName => {
                            const tagObj = tags.find(t => t.name === tagName);
                            return (
                              <span key={tagName} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: tagObj?.color || '#A0AEC0', backgroundColor: `${tagObj?.color || '#A0AEC0'}15` }}>
                                {tagName}
                              </span>
                            );
                          })}
                          {item.tags.length > 3 && <span className="text-[10px] text-[#5A6C7D]">+{item.tags.length - 3}</span>}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {!isLoading && viewMode === 'list' && filteredContent.length > 0 && (
          <div className="space-y-2">
            {filteredContent.map((item, index) => {
              // Extract first two sentences for preview
              const getPreviewText = (text) => {
                if (!text) return '';
                const sentences = text.split(/[.!?]+/).filter(s => s.trim());
                return sentences.slice(0, 2).join('. ').trim() + (sentences.length > 0 ? '.' : '');
              };
              const previewText = getPreviewText(item.text_content);
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={() => isSelectMode ? toggleSelectId(item.id) : openDetailPanel(item)}
                  className={`flex items-start gap-4 p-4 bg-[#1A2B42] hover:border-[#00D9C8] border cursor-pointer transition-colors ${
                    isSelectMode && selectedIds.has(item.id) ? 'border-[#00D9C8] ring-1 ring-[#00D9C8]/50' : 'border-transparent'
                  }`}
                  data-testid={`content-row-${item.id}`}
                >
                  {/* Selection checkbox */}
                  {isSelectMode && (
                    <div className={`w-5 h-5 border-2 rounded flex-shrink-0 flex items-center justify-center mt-1 transition-colors ${
                      selectedIds.has(item.id) 
                        ? 'bg-[#00D9C8] border-[#00D9C8]' 
                        : 'border-[#5A6C7D] bg-transparent'
                    }`} data-testid={`select-checkbox-${item.id}`}>
                      {selectedIds.has(item.id) && <Check className="w-3 h-3 text-[#0A1628]" />}
                    </div>
                  )}
                  {/* Content Number */}
                  <span className="text-xs font-mono text-[#00D9C8] flex-shrink-0 w-8">
                    #{item.content_number || '—'}
                  </span>
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-1">
                    {getPlatformIcon(item.platform)}
                  </div>
                  <div className="flex-1 min-w-0">
                    {(() => {
                      const pConfig = PLATFORM_CONFIG[item.platform];
                      const showHeadline = pConfig ? pConfig.hasHeadline : item.platform?.startsWith('custom_');
                      if (showHeadline) {
                        return (
                          <>
                            <h3 className="text-sm font-medium text-white mb-1 leading-tight">
                              {item.content_headline || item.title}
                            </h3>
                            {previewText && (
                              <p className="text-xs text-[#A0AEC0] line-clamp-2 leading-relaxed">
                                {previewText}
                              </p>
                            )}
                          </>
                        );
                      }
                      return (
                        <p className="text-sm text-[#A0AEC0] line-clamp-2 leading-relaxed">
                          {previewText || item.text_content?.substring(0, 120)}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.tags?.slice(0, 2).map(tagName => {
                      const tagObj = tags.find(t => t.name === tagName);
                      return (
                        <span key={tagName} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: tagObj?.color || '#A0AEC0', backgroundColor: `${tagObj?.color || '#A0AEC0'}15` }}>
                          {tagName}
                        </span>
                      );
                    })}
                  </div>
                  <Badge className={`flex-shrink-0 ${statusConfig[item.status]?.color || 'bg-gray-500/20 text-gray-500'}`}>
                    {statusConfig[item.status]?.label || item.status}
                  </Badge>
                  <span className="text-xs text-[#5A6C7D] flex-shrink-0">
                    {formatDate(item.created_at)}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Panel - Full screen on mobile */}
      <AnimatePresence>
        {selectedContent && (
          <motion.div
            initial={{ x: 480 }}
            animate={{ x: 0 }}
            exit={{ x: 480 }}
            className="fixed right-0 top-0 bottom-0 w-full lg:w-[480px] bg-[#0A1628] border-l border-[#1A2B42] overflow-y-auto z-50"
            data-testid="detail-panel"
          >
            {/* Panel Header */}
            <div className="sticky top-0 bg-[#0A1628] border-b border-[#1A2B42] p-3 lg:p-4 flex items-center justify-between">
              <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                {getPlatformIcon(selectedContent.platform)}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs lg:text-sm font-mono text-[#00D9C8]" data-testid="detail-content-number">
                    #{selectedContent.content_number || '—'}
                  </span>
                  <h2 className="font-bold text-white truncate text-sm lg:text-base max-w-[200px] lg:max-w-[280px]">
                    {selectedContent.title}
                  </h2>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setShowExpandedModal(true)}
                  className="p-2 text-[#A0AEC0] hover:text-[#00D9C8] transition-colors"
                  title={t.produktion.expand}
                  data-testid="expand-modal-btn"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setMovingContentId(selectedContent.id); setShowFolderMoveModal(true); }}
                  className="p-2 text-[#A0AEC0] hover:text-[#00D9C8] transition-colors"
                  title={t.produktion.moveToFolder}
                  data-testid="move-to-folder-btn"
                >
                  <FolderInput className="w-4 h-4" />
                </button>
                <button 
                  onClick={closeDetailPanel}
                  className="p-2 text-[#A0AEC0] hover:text-white flex-shrink-0"
                  data-testid="close-panel-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#1A2B42]">
              {['text', 'visual', 'meta', 'kommentare'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'text-[#00D9C8] border-b-2 border-[#00D9C8]'
                      : 'text-[#A0AEC0] hover:text-white'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'text' ? 'Text' : tab === 'visual' ? (lang === 'de' ? 'Bild' : 'Image') : tab === 'meta' ? 'Meta' : (lang === 'de' ? 'Komm.' : 'Comm.')}
                  {tab === 'kommentare' && comments.length > 0 && (
                    <span className="ml-1 lg:ml-1.5 px-1 lg:px-1.5 py-0.5 text-[10px] lg:text-xs bg-[#00D9C8]/20 text-[#00D9C8] rounded-full">
                      {comments.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-3 lg:p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              {/* Text Tab */}
              {activeTab === 'text' && (
                <div className="space-y-4">
                  {/* Pressemitteilung (Press Release) - Structured Editor */}
                  {selectedContent.platform === 'pressemitteilung' ? (
                    <div className="space-y-5" data-testid="press-release-editor">
                      {PRESS_RELEASE_FIELDS.map(field => {
                        const value = editedStructuredContent[field.key] || '';
                        
                        // Dynamische Höhe basierend auf Feldtyp
                        const getMinHeight = () => {
                          if (field.key === 'body_text') return '280px';
                          if (field.key === 'short_text' || field.key === 'company_description') return '160px';
                          if (field.key === 'subtitle' || field.key === 'keywords') return '80px';
                          return '48px';
                        };
                        
                        return (
                          <div key={field.key} className="space-y-2">
                            <div className="flex items-center justify-between bg-[#0A1628] py-1">
                              <Label className="text-[#A0AEC0] text-sm font-medium flex items-center gap-2">
                                {field.label}
                                {field.optional && <span className="text-[#5A6C7D] text-xs">(optional)</span>}
                              </Label>
                              {field.multiline && (
                                <button
                                  onClick={() => handleRegenerateField(field.key, field.label)}
                                  disabled={regeneratingField === field.key}
                                  className="p-1 text-[#5A6C7D] hover:text-[#00D9C8] transition-colors"
                                  title={`${field.label} neu generieren`}
                                  data-testid={`regenerate-field-${field.key}`}
                                >
                                  {regeneratingField === field.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                            {field.multiline ? (
                              <RichTextFieldEditor
                                content={value}
                                onChange={(html) => setEditedStructuredContent(prev => ({
                                  ...prev,
                                  [field.key]: html
                                }))}
                                maxChars={field.max}
                                placeholder={field.label}
                                minHeight={getMinHeight()}
                                testId={`press-release-${field.key}`}
                              />
                            ) : (
                              <Input
                                value={value}
                                onChange={(e) => setEditedStructuredContent(prev => ({
                                  ...prev,
                                  [field.key]: e.target.value
                                }))}
                                className={`bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm ${
                                  value.length > field.max ? 'ring-2 ring-red-400' : ''
                                }`}
                                placeholder={field.label}
                                data-testid={`press-release-${field.key}`}
                              />
                            )}
                          </div>
                        );
                      })}
                      
                      <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                        <Button
                          onClick={handleSaveStructuredContent}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-press-release-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-press-release-btn"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : selectedContent.platform === 'pinterest' ? (
                    /* Pinterest - Structured Editor (Title + Description) */
                    <div className="space-y-5" data-testid="pinterest-editor">
                      {/* Pin Title */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[#A0AEC0] text-sm font-medium">Pin-Titel</Label>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            (editedStructuredContent.pin_title || '').length > 100 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-[#1A2B42] text-[#5A6C7D]'
                          }`}>
                            {(editedStructuredContent.pin_title || '').length} / 100
                          </span>
                        </div>
                        <Input
                          value={editedStructuredContent.pin_title || ''}
                          onChange={(e) => setEditedStructuredContent(prev => ({
                            ...prev,
                            pin_title: e.target.value.slice(0, 100)
                          }))}
                          maxLength={100}
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                          placeholder="Ansprechender Pin-Titel..."
                          data-testid="pinterest-pin-title"
                        />
                      </div>
                      
                      {/* Pin Description */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-[#A0AEC0] text-sm font-medium">Pin-Beschreibung</Label>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            (editedStructuredContent.pin_description || '').length > 500 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-[#1A2B42] text-[#5A6C7D]'
                          }`}>
                            {(editedStructuredContent.pin_description || '').length} / 500
                          </span>
                        </div>
                        <Textarea
                          value={editedStructuredContent.pin_description || ''}
                          onChange={(e) => setEditedStructuredContent(prev => ({
                            ...prev,
                            pin_description: e.target.value.slice(0, 500)
                          }))}
                          maxLength={500}
                          style={{ minHeight: '160px' }}
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                          placeholder="Inspirierende Beschreibung mit Mehrwert..."
                          data-testid="pinterest-pin-description"
                        />
                      </div>
                      
                      {/* Pin Link */}
                      <div className="space-y-2">
                        <Label className="text-[#A0AEC0] text-sm font-medium">Link (optional)</Label>
                        <Input
                          value={editedStructuredContent.pin_link || ''}
                          onChange={(e) => setEditedStructuredContent(prev => ({
                            ...prev,
                            pin_link: e.target.value
                          }))}
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                          placeholder="https://www.beispiel.de/seite"
                          data-testid="pinterest-pin-link"
                        />
                        <p className="text-[#5A6C7D] text-xs">Wohin soll der Pin verlinken?</p>
                      </div>

                      {(editedStructuredContent.pin_title || '').length > 100 && (
                        <p className="text-red-400 text-xs font-medium">
                          Pin-Titel: {(editedStructuredContent.pin_title || '').length - 100} Zeichen zu viel!
                        </p>
                      )}
                      
                      <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                        <Button
                          onClick={handleSaveStructuredContent}
                          disabled={isSaving || (editedStructuredContent.pin_title || '').length > 100 || (editedStructuredContent.pin_description || '').length > 500}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-pinterest-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : selectedContent.platform === 'youtube' ? (
                    /* YouTube - Script Editor */
                    <div className="space-y-5" data-testid="youtube-editor">
                      {selectedContent.status === 'video_generation' ? (
                        /* Read-only mode when submitted for generation */
                        <div className="space-y-4">
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-purple-500/20 text-purple-400">Zur Generierung</Badge>
                            </div>
                            <p className="text-sm text-purple-300">Dieses Skript wurde zur Video-Generierung aufgegeben und kann nicht mehr bearbeitet werden.</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Video-Titel</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm">
                              {editedStructuredContent.youtube_title || 'Kein Titel'}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Skript (max 30 Sek.)</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm whitespace-pre-wrap">
                              {editedStructuredContent.youtube_script || 'Kein Skript'}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">YouTube-Beschreibung</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm whitespace-pre-wrap max-h-[150px] overflow-y-auto">
                              {editedStructuredContent.youtube_description || 'Keine Beschreibung'}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Tags</Label>
                            <div className="bg-[#1A2B42] p-3 rounded text-white text-sm">
                              {editedStructuredContent.youtube_tags || 'Keine Tags'}
                            </div>
                          </div>
                          {selectedContent.video_url ? (
                            <div className="space-y-2">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Video</Label>
                              <video src={selectedContent.video_url} controls className="w-full rounded-lg border border-[#1A2B42]" />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Video</Label>
                              <label className="cursor-pointer" data-testid="youtube-video-upload-readonly">
                                <input type="file" accept="video/*" className="hidden"
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 100 * 1024 * 1024) { toast.error('Video zu groß (max. 100MB)'); return; }
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    try {
                                      setIsSaving(true);
                                      const res = await axios.post(`${API}/content/${selectedContent.id}/upload-video`, fd, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 120000
                                      });
                                      setSelectedContent(prev => ({ ...prev, video_url: res.data.video_url, status: 'draft' }));
                                      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, video_url: res.data.video_url, status: 'draft' } : c));
                                      toast.success('Video hochgeladen');
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                                    finally { setIsSaving(false); }
                                  }}
                                />
                                <div className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#1A2B42] rounded-lg hover:border-purple-500/40 transition-colors bg-[#0A1628]">
                                  <Upload className="w-8 h-8 text-[#5A6C7D]" />
                                  <span className="text-sm text-[#A0AEC0]">Video hochladen</span>
                                  <span className="text-xs text-[#5A6C7D]">MP4, MOV, etc. (max. 100MB)</span>
                                </div>
                              </label>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Editable mode */
                        <>
                          {/* Video Title */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Video-Titel</Label>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (editedStructuredContent.youtube_title || '').length > 100 
                                  ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2B42] text-[#5A6C7D]'
                              }`}>
                                {(editedStructuredContent.youtube_title || '').length} / 100
                              </span>
                            </div>
                            <Input
                              value={editedStructuredContent.youtube_title || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_title: e.target.value
                              }))}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                              placeholder="Ansprechender YouTube-Titel..."
                              maxLength={100}
                              data-testid="youtube-title-input"
                            />
                          </div>
                          {/* Script */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[#A0AEC0] text-sm font-medium">Skript (max 30 Sek.)</Label>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (editedStructuredContent.youtube_script || '').length > 450 
                                  ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2B42] text-[#5A6C7D]'
                              }`}>
                                {(editedStructuredContent.youtube_script || '').length} / 450
                              </span>
                            </div>
                            <Textarea
                              value={editedStructuredContent.youtube_script || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_script: e.target.value
                              }))}
                              style={{ minHeight: '120px' }}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                              placeholder="Dein Video-Skript zum Vorlesen..."
                              data-testid="youtube-script-input"
                            />
                          </div>
                          {/* Description */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-[#A0AEC0] text-sm font-medium">YouTube-Beschreibung</Label>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                (editedStructuredContent.youtube_description || '').length > 5000 
                                  ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2B42] text-[#5A6C7D]'
                              }`}>
                                {(editedStructuredContent.youtube_description || '').length} / 5.000
                              </span>
                            </div>
                            <Textarea
                              value={editedStructuredContent.youtube_description || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_description: e.target.value
                              }))}
                              style={{ minHeight: '100px' }}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                              placeholder="YouTube-Beschreibung mit Links und Hashtags..."
                              data-testid="youtube-description-input"
                            />
                          </div>
                          {/* Tags */}
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Tags (SEO)</Label>
                            <Input
                              value={editedStructuredContent.youtube_tags || ''}
                              onChange={(e) => setEditedStructuredContent(prev => ({
                                ...prev, youtube_tags: e.target.value
                              }))}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                              placeholder="keyword1, keyword2, keyword3"
                              data-testid="youtube-tags-input"
                            />
                          </div>
                          {/* Video Upload / Preview */}
                          <div className="space-y-2">
                            <Label className="text-[#A0AEC0] text-sm font-medium">Video</Label>
                            {selectedContent.video_url ? (
                              <div className="space-y-2">
                                <video src={selectedContent.video_url} controls className="w-full rounded-lg border border-[#1A2B42]" />
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Video hochgeladen</span>
                                  <label className="cursor-pointer">
                                    <input type="file" accept="video/*" className="hidden" data-testid="youtube-video-replace"
                                      onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        if (f.size > 100 * 1024 * 1024) { toast.error('Video zu groß (max. 100MB)'); return; }
                                        const fd = new FormData();
                                        fd.append('file', f);
                                        try {
                                          setIsSaving(true);
                                          const res = await axios.post(`${API}/content/${selectedContent.id}/upload-video`, fd, {
                                            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 120000
                                          });
                                          setSelectedContent(prev => ({ ...prev, video_url: res.data.video_url, status: 'draft' }));
                                          setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, video_url: res.data.video_url, status: 'draft' } : c));
                                          toast.success('Video ersetzt');
                                        } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                                        finally { setIsSaving(false); }
                                      }}
                                    />
                                    <span className="text-xs text-[#00D9C8] hover:underline">Video ersetzen</span>
                                  </label>
                                </div>
                              </div>
                            ) : (
                              <label className="cursor-pointer" data-testid="youtube-video-upload-label">
                                <input type="file" accept="video/*" className="hidden" data-testid="youtube-video-input"
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 100 * 1024 * 1024) { toast.error('Video zu groß (max. 100MB)'); return; }
                                    const fd = new FormData();
                                    fd.append('file', f);
                                    try {
                                      setIsSaving(true);
                                      const res = await axios.post(`${API}/content/${selectedContent.id}/upload-video`, fd, {
                                        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }, timeout: 120000
                                      });
                                      setSelectedContent(prev => ({ ...prev, video_url: res.data.video_url, status: 'draft' }));
                                      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, video_url: res.data.video_url, status: 'draft' } : c));
                                      toast.success('Video hochgeladen');
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
                                    finally { setIsSaving(false); }
                                  }}
                                />
                                <div className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#1A2B42] rounded-lg hover:border-[#00D9C8]/40 transition-colors bg-[#0A1628]">
                                  <Upload className="w-8 h-8 text-[#5A6C7D]" />
                                  <span className="text-sm text-[#A0AEC0]">Video hochladen</span>
                                  <span className="text-xs text-[#5A6C7D]">MP4, MOV, etc. (max. 100MB)</span>
                                </div>
                              </label>
                            )}
                          </div>
                          <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                            <Button
                              onClick={handleSaveStructuredContent}
                              disabled={isSaving}
                              className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                              data-testid="save-youtube-btn"
                            >
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                            </Button>
                            <Button
                              onClick={handleRegenerateText}
                              disabled={isRegenerating}
                              variant="outline"
                              className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                            >
                              {isRegenerating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-1" />
                              )}
                              Neu generieren
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : selectedContent.platform?.startsWith('custom_') ? (
                    /* Custom Channel - Structured Editor with custom fields */
                    <div className="space-y-5" data-testid="custom-channel-editor">
                      {(() => {
                        const cc = customChannels.find(c => c.id === selectedContent.platform);
                        if (!cc) return <p className="text-[#5A6C7D] text-sm">{lang === 'de' ? 'Kanal-Konfiguration nicht gefunden.' : 'Channel configuration not found.'}</p>;
                        const customFields = selectedContent.custom_fields || {};
                        return cc.fields.map((field, idx) => {
                          const fieldValue = editedStructuredContent[`custom_${field.name}`] ?? customFields[field.name] ?? '';
                          const charCount = fieldValue.length;
                          const isOverLimit = charCount > field.maxLength;
                          const isNearLimit = charCount > field.maxLength * 0.9;
                          return (
                            <div key={field.name} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-[#A0AEC0] text-sm font-medium">{field.name}</Label>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  isOverLimit ? 'bg-red-500/20 text-red-400' : 
                                  isNearLimit ? 'bg-yellow-500/20 text-yellow-400' : 
                                  'bg-[#1A2B42] text-[#5A6C7D]'
                                }`}>
                                  {charCount.toLocaleString()} / {field.maxLength.toLocaleString()}
                                </span>
                              </div>
                              {field.maxLength > 200 ? (
                                <Textarea
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setEditedStructuredContent(prev => ({
                                      ...prev, [`custom_${field.name}`]: e.target.value
                                    }));
                                  }}
                                  style={{ minHeight: field.maxLength > 1000 ? '200px' : '100px' }}
                                  className={`bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm resize-y ${isOverLimit ? 'ring-2 ring-red-400' : ''}`}
                                  placeholder={field.name}
                                  data-testid={`custom-field-${field.name}`}
                                />
                              ) : (
                                <Input
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setEditedStructuredContent(prev => ({
                                      ...prev, [`custom_${field.name}`]: e.target.value
                                    }));
                                  }}
                                  className={`bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm ${isOverLimit ? 'ring-2 ring-red-400' : ''}`}
                                  placeholder={field.name}
                                  data-testid={`custom-field-${field.name}`}
                                />
                              )}
                            </div>
                          );
                        });
                      })()}
                      <div className="flex gap-2 pt-6 mt-4 border-t border-[#1A2B42]">
                        <Button
                          onClick={handleSaveCustomFields}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-custom-content-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-custom-content-btn"
                        >
                          {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </div>
                  ) : selectedContent.platform === 'blog' ? (
                    /* Blog WYSIWYG Editor */
                    <>
                      {/* Blog Title */}
                      <div>
                        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Titel (optional)</Label>
                        <Input
                          value={blogTitle}
                          onChange={(e) => setBlogTitle(e.target.value)}
                          placeholder="Blogartikel-Titel eingeben..."
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-lg font-semibold"
                          data-testid="blog-title-input"
                        />
                      </div>

                      {/* Blog Subtitle */}
                      <div>
                        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Überschrift (optional)</Label>
                        <Input
                          value={blogSubtitle}
                          onChange={(e) => setBlogSubtitle(e.target.value)}
                          placeholder="Untertitel / Überschrift eingeben..."
                          className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-base"
                          data-testid="blog-subtitle-input"
                        />
                      </div>

                      {/* WYSIWYG Rich Text Editor */}
                      <BlogEditor
                        content={editedText}
                        onChange={setEditedText}
                        maxChars={getPlatformLimit(selectedContent.platform)}
                      />

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSaveText}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-text-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-text-btn"
                        >
                          {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* Standard Text Editor for other platforms */
                    <>
                      <Textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                        className="min-h-[300px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-none"
                        data-testid="content-text-editor"
                      />
                      {/* Character Counter */}
                      {(() => {
                        const charCount = editedText.length;
                        const maxChars = getPlatformLimit(selectedContent.platform);
                        const isOverLimit = charCount > maxChars;
                        const isNearLimit = charCount > maxChars * 0.9;
                        
                        return (
                          <div className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                            isOverLimit ? 'bg-red-500/20 text-red-400' : 
                            isNearLimit ? 'bg-yellow-500/20 text-yellow-400' : 
                            'bg-[#0A1628] text-[#5A6C7D]'
                          }`}>
                            <span>
                              {charCount.toLocaleString()} / {maxChars.toLocaleString()} Zeichen
                            </span>
                            {isOverLimit && (
                              <span className="font-medium">
                                {(charCount - maxChars).toLocaleString()} zu viel!
                              </span>
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex gap-2">
                        <Button
                          onClick={handleSaveText}
                          disabled={isSaving}
                          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                          data-testid="save-text-btn"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                        </Button>
                        <Button
                          onClick={handleRegenerateText}
                          disabled={isRegenerating}
                          variant="outline"
                          className="border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                          data-testid="regenerate-text-btn"
                        >
                          {isRegenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          {t.common.regenerate}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* KI-Anweisung — Text anpassen */}
                  <div className="bg-[#1A2B42]/50 rounded-lg p-3 space-y-2" data-testid="refine-text-section">
                    <div className="flex items-center gap-2 mb-1">
                      <Wand2 className="w-3.5 h-3.5 text-[#00D9C8]" />
                      <span className="text-xs text-[#A0AEC0] font-medium">
                        {t.produktion.aiRefineText}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={refineInstruction}
                        onChange={(e) => setRefineInstruction(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && refineInstruction.trim()) { e.preventDefault(); handleRefine('text'); } }}
                        placeholder={t.produktion.aiRefinePlaceholder}
                        className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm h-9"
                        disabled={isRefining}
                        data-testid="refine-instruction-input"
                      />
                      <Button
                        onClick={() => handleRefine('text')}
                        disabled={isRefining || !refineInstruction.trim()}
                        size="sm"
                        className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-9 px-3"
                        data-testid="refine-text-btn"
                      >
                        {isRefining && refineType === 'text' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Version History */}
                  {versions.length > 0 && (
                    <div>
                      <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.versions}</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0]">
                            <Clock className="w-4 h-4 mr-2" />
                            {versions.length} Version{versions.length !== 1 ? 'en' : ''}
                            <ChevronDown className="w-4 h-4 ml-auto" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628] w-full">
                          {versions.map(v => (
                            <DropdownMenuItem 
                              key={v.id}
                              onClick={() => loadVersion(v)}
                              className="text-white hover:bg-[#0A1628]"
                            >
                              Version {v.version_number} - {formatDate(v.created_at)}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              )}

              {/* Visual Tab */}
              {activeTab === 'visual' && (
                <div className="space-y-4">
                  {/* Selection Info */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#A0AEC0]">
                      {selectedImages.length} {lang === 'de' ? 'von max.' : 'of max.'} {getMaxImages()} {lang === 'de' ? `Bild${getMaxImages() !== 1 ? 'ern' : ''}` : `image${getMaxImages() !== 1 ? 's' : ''}`} {lang === 'de' ? 'ausgewählt' : 'selected'}
                    </span>
                    {selectedImages.length > 0 && (
                      <button 
                        onClick={() => setSelectedImages([])}
                        className="text-[#00D9C8] hover:underline text-xs"
                      >
                        Auswahl aufheben
                      </button>
                    )}
                  </div>

                  {/* Images Grid with Selection */}
                  {selectedContent.image_urls?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {selectedContent.image_urls.map((url, idx) => {
                        const isSelected = selectedImages.includes(url);
                        return (
                          <div 
                            key={idx} 
                            className={`relative group aspect-square bg-[#1A2B42] cursor-pointer transition-all ${
                              isSelected ? 'ring-2 ring-[#00D9C8]' : 'hover:ring-1 hover:ring-[#5A6C7D]'
                            }`}
                            onClick={() => toggleImageSelection(url)}
                          >
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            
                            {/* Number badge */}
                            <div className="absolute top-2 left-2 w-6 h-6 bg-[#00D9C8] rounded-full flex items-center justify-center text-xs font-bold text-[#0A1628]">
                              {idx + 1}
                            </div>

                            {/* Selection Checkbox - Top Right */}
                            <div 
                              className={`absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'bg-[#00D9C8] text-[#0A1628]' 
                                  : 'bg-[#0A1628]/80 text-white border border-[#5A6C7D] group-hover:border-[#00D9C8]'
                              }`}
                            >
                              {isSelected && <Check className="w-4 h-4" />}
                            </div>

                            {/* Reorder buttons */}
                            {selectedContent.image_urls.length > 1 && (
                              <div className="absolute bottom-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {idx > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const imgs = [...selectedContent.image_urls];
                                      [imgs[idx - 1], imgs[idx]] = [imgs[idx], imgs[idx - 1]];
                                      setSelectedContent(prev => ({ ...prev, image_urls: imgs }));
                                      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, image_urls: imgs } : c));
                                      // Update selected images order accordingly
                                      setSelectedImages(prev => prev.map(u => u === imgs[idx] ? imgs[idx - 1] : u === imgs[idx - 1] ? imgs[idx] : u));
                                    }}
                                    className="w-6 h-6 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded flex items-center justify-center text-white text-xs"
                                  >
                                    &larr;
                                  </button>
                                )}
                                {idx < selectedContent.image_urls.length - 1 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const imgs = [...selectedContent.image_urls];
                                      [imgs[idx], imgs[idx + 1]] = [imgs[idx + 1], imgs[idx]];
                                      setSelectedContent(prev => ({ ...prev, image_urls: imgs }));
                                      setContent(prev => prev.map(c => c.id === selectedContent.id ? { ...c, image_urls: imgs } : c));
                                      setSelectedImages(prev => prev.map(u => u === imgs[idx + 1] ? imgs[idx] : u === imgs[idx] ? imgs[idx + 1] : u));
                                    }}
                                    className="w-6 h-6 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded flex items-center justify-center text-white text-xs"
                                  >
                                    &rarr;
                                  </button>
                                )}
                              </div>
                            )}

                            {/* Hover Actions */}
                            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                              <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    refineImageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    setTimeout(() => refineImageInputRef.current?.focus(), 300);
                                  }}
                                  className="p-1.5 bg-[#0A1628]/90 hover:bg-[#00D9C8] transition-colors rounded"
                                  title={lang === 'de' ? 'Neues Bild generieren' : 'Generate new image'}
                                  data-testid={`magic-wand-image-${idx}`}
                                >
                                  <Wand2 className="w-3 h-3 text-white" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingImageUrl(url);
                                    editImageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    setTimeout(() => editImageInputRef.current?.focus(), 300);
                                  }}
                                  className="p-1.5 bg-[#0A1628]/90 hover:bg-[#7C3AED] transition-colors rounded"
                                  title={lang === 'de' ? 'Dieses Bild bearbeiten' : 'Edit this image'}
                                  data-testid={`edit-image-${idx}`}
                                >
                                  <Pencil className="w-3 h-3 text-white" />
                                </button>
                              <a 
                                href={url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-1.5 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Maximize className="w-3 h-3 text-white" />
                              </a>
                              <a 
                                href={url} 
                                download 
                                className="p-1.5 bg-[#0A1628]/90 hover:bg-[#0A1628] rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="w-3 h-3 text-white" />
                              </a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteImage(url);
                                }}
                                className="p-1.5 bg-[#0A1628]/90 hover:bg-red-500 transition-colors rounded"
                                title={lang === 'de' ? 'Bild löschen' : 'Delete image'}
                                data-testid={`delete-image-${idx}`}
                              >
                                <Trash className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-[#1A2B42]">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 text-[#5A6C7D]" />
                      <p className="text-[#A0AEC0] text-sm">{lang === 'de' ? 'Keine Bilder vorhanden' : 'No images available'}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {/* AI Image Generation Button */}
                    <Button
                      variant="outline"
                      onClick={() => handleGenerateImage(selectedContent.id)}
                      disabled={generatingImageId === selectedContent.id || !selectedContent.image_prompt}
                      className="flex-1 border-[#00D9C8] text-[#00D9C8] hover:bg-[#00D9C8]/10"
                      data-testid="generate-image-btn"
                    >
                      {generatingImageId === selectedContent.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generiere...
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-4 h-4 mr-2" />
                          {t.produktion.aiGenerateImage}
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="flex gap-2">
                    <label className="flex-1">
                      <Button variant="outline" className="w-full border-[#1A2B42] text-[#A0AEC0]" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          {lang === 'de' ? 'Bild hochladen' : 'Upload image'}
                        </span>
                      </Button>
                      <input type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
                    </label>
                    <Button
                      variant="outline"
                      onClick={() => setShowGalleryModal(true)}
                      className="flex-1 border-[#1A2B42] text-[#A0AEC0]"
                    >
                      Aus Galerie
                    </Button>
                  </div>

                  {/* KI-Anweisung — Bild anpassen */}
                  <div className="bg-[#1A2B42]/50 rounded-lg p-3 space-y-2" data-testid="refine-image-section">
                    <div className="flex items-center gap-2 mb-1">
                      <Wand2 className="w-3.5 h-3.5 text-[#00D9C8]" />
                      <span className="text-xs text-[#A0AEC0] font-medium">
                        {t.produktion.aiGenerateImageTitle}
                      </span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30 uppercase tracking-wide" data-testid="refine-image-beta-badge">
                        Beta Phase
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        ref={refineImageInputRef}
                        value={refineImageInstruction}
                        onChange={(e) => setRefineImageInstruction(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && refineImageInstruction.trim()) { e.preventDefault(); handleRefine('image', refineImageInstruction); } }}
                        placeholder={t.produktion.aiGenerateImagePlaceholder}
                        className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm h-9"
                        disabled={isRefining}
                        data-testid="refine-image-instruction-input"
                      />
                      <Button
                        onClick={() => handleRefine('image', refineImageInstruction)}
                        disabled={isRefining || !refineImageInstruction.trim()}
                        size="sm"
                        className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-9 px-3"
                        data-testid="refine-image-btn"
                      >
                        {isRefining && refineType === 'image' ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                  {/* KI-Anweisung — Bestehendes Bild bearbeiten */}
                  <div className="bg-[#1A2B42]/50 rounded-lg p-3 space-y-2" data-testid="edit-image-section">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Pencil className="w-3.5 h-3.5 text-[#7C3AED]" />
                        <span className="text-xs text-[#A0AEC0] font-medium">
                          {lang === 'de' ? 'Bestehendes Bild bearbeiten' : 'Edit existing image'}
                        </span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30 uppercase tracking-wide" data-testid="edit-image-beta-badge">
                          Beta Phase
                        </span>
                      </div>
                      {editingImageUrl && (
                        <div className="flex items-center gap-1.5">
                          <img src={editingImageUrl} className="w-6 h-6 rounded object-cover opacity-70" alt="" />
                          <button
                            onClick={() => { setEditingImageUrl(null); setEditImageInstruction(''); }}
                            className="text-[#5A6C7D] hover:text-white text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                    {!editingImageUrl && (
                      <p className="text-xs text-[#5A6C7D]">
                        {lang === 'de'
                          ? '↑ Klicke auf das Stift-Icon eines Bildes um es zu bearbeiten'
                          : '↑ Click the pencil icon on an image to edit it'}
                      </p>
                    )}
                    {editingImageUrl && (
                      <div className="flex gap-2">
                        <Input
                          ref={editImageInputRef}
                          value={editImageInstruction}
                          onChange={(e) => setEditImageInstruction(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && editImageInstruction.trim()) {
                              e.preventDefault();
                              handleEditImage();
                            }
                          }}
                          placeholder={lang === 'de'
                            ? 'z.B. "Hintergrund weiß machen", "Logo entfernen"'
                            : 'e.g. "Make background white", "Remove logo"'}
                          className="flex-1 bg-[#0A1628] border-transparent focus:border-[#7C3AED] text-white text-sm h-9"
                          disabled={isEditingImage}
                          data-testid="edit-image-instruction-input"
                        />
                        <Button
                          onClick={handleEditImage}
                          disabled={isEditingImage || !editImageInstruction.trim()}
                          size="sm"
                          className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white h-9 px-3"
                          data-testid="edit-image-btn"
                        >
                          {isEditingImage ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Pencil className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Save Images Button */}
                  <Button
                    onClick={handleSaveImages}
                    disabled={isSaving}
                    className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-medium"
                    data-testid="save-images-btn"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    {t.produktion.saveImages}
                  </Button>
                </div>
              )}

              {/* Meta Tab */}
              {activeTab === 'meta' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{t.produktion.platform}</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full border-[#1A2B42] bg-[#1A2B42] text-white justify-between">
                          <span className="flex items-center gap-2">
                            {getPlatformIcon(editedMeta.platform)}
                            {platforms.find(p => p.key === editedMeta.platform)?.label}
                          </span>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                        {activePlatforms.map(p => (
                          <DropdownMenuItem 
                            key={p.key}
                            onClick={() => {
                              const newConfig = getPlatformConfig(p.key);
                              const textContent = editedText || selectedContent?.text_content || '';
                              if (textContent.length > newConfig.maxLength) {
                                toast.warning(
                                  lang === 'de' 
                                    ? `Achtung: Dein Text hat ${textContent.length.toLocaleString()} Zeichen, ${newConfig.name || p.label} erlaubt max. ${newConfig.maxLength.toLocaleString()}.`
                                    : `Warning: Your text has ${textContent.length.toLocaleString()} characters, ${newConfig.name || p.label} allows max. ${newConfig.maxLength.toLocaleString()}.`
                                );
                              }
                              setEditedMeta(prev => ({ ...prev, platform: p.key }));
                            }}
                            className="text-white hover:bg-[#0A1628]"
                          >
                            {getPlatformIcon(p.key)}
                            <span className="ml-2">{p.label}</span>
                            <span className="ml-auto text-[10px] text-[#5A6C7D]">{getPlatformConfig(p.key).maxLength}</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {/* Platform rules info */}
                    {editedMeta.platform && (() => {
                      const cfg = getPlatformConfig(editedMeta.platform);
                      const textLen = (editedText || selectedContent?.text_content || '').length;
                      const overLimit = textLen > cfg.maxLength;
                      return (
                        <div className={`mt-2 p-2 rounded text-xs ${overLimit ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-[#0A1628] text-[#5A6C7D]'}`} data-testid="platform-rules-info">
                          <span>{t.common.max} {cfg.maxLength.toLocaleString()} {t.common.chars}</span>
                          {cfg.imageLimit > 0 && <span className="ml-3">| {cfg.imageLimit} {t.common.images}</span>}
                          {cfg.hashtags > 0 && <span className="ml-3">| {cfg.hashtags} Hashtags</span>}
                          {overLimit && <span className="ml-3 font-medium">({textLen.toLocaleString()} / {cfg.maxLength.toLocaleString()})</span>}
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{lang === 'de' ? 'Zielgruppe' : 'Target audience'}</Label>
                    <Input
                      value={editedMeta.target_audience}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, target_audience: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white"
                      placeholder={lang === 'de' ? 'z.B. Marketing Manager, Entscheider' : 'e.g. Marketing Manager, Decision Makers'}
                    />
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{lang === 'de' ? 'Ziel' : 'Goal'}</Label>
                    <Input
                      value={editedMeta.goal}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, goal: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white"
                      placeholder={lang === 'de' ? 'z.B. Lead Generation, Awareness' : 'e.g. Lead Generation, Awareness'}
                    />
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">Geplantes Datum</Label>
                    <Input
                      type="datetime-local"
                      value={editedMeta.scheduled_at?.slice(0, 16) || ''}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, scheduled_at: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-2 block">{lang === 'de' ? 'Quellinformation' : 'Source information'}</Label>
                    <Textarea
                      value={editedMeta.source_info}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, source_info: e.target.value }))}
                      className="min-h-[80px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-none"
                      placeholder={lang === 'de' ? 'Ursprüngliche Beschreibung oder Quelle' : 'Original description or source'}
                    />
                  </div>

                  {/* Tags */}
                  <div data-testid="meta-tags-section">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block flex items-center gap-1.5">
                      <Tag className="w-3 h-3" /> Tags
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tag => {
                        const isActive = (selectedContent.tags || []).includes(tag.name);
                        return (
                          <button
                            key={tag.id}
                            onClick={() => handleToggleTag(selectedContent.id, tag.name)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                              isActive 
                                ? 'border-current opacity-100' 
                                : 'border-transparent opacity-50 hover:opacity-80'
                            }`}
                            style={{ color: tag.color, backgroundColor: `${tag.color}15` }}
                            data-testid={`meta-tag-${tag.id}`}
                          >
                            {tag.name} {isActive && <span className="ml-0.5">&#10003;</span>}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setShowTagManager(true)}
                        className="text-xs px-2 py-1 rounded-full text-[#5A6C7D] hover:text-[#00D9C8] border border-dashed border-[#5A6C7D] hover:border-[#00D9C8] transition-colors"
                        data-testid="meta-add-tag-btn"
                      >
                        + Tag
                      </button>
                    </div>
                  </div>

                  {/* Folder */}
                  <div data-testid="meta-folder-section">
                    <Label className="text-[#A0AEC0] text-xs mb-2 block flex items-center gap-1.5">
                      <FolderOpen className="w-3 h-3" /> {lang === 'de' ? 'Ordner' : 'Folder'}
                    </Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full border-[#1A2B42] bg-[#1A2B42] text-[#A0AEC0] justify-between text-xs">
                          <span className="flex items-center gap-1.5">
                            <FolderOpen className="w-3.5 h-3.5" />
                            {selectedContent.folder_id 
                              ? folders.find(f => f.id === selectedContent.folder_id)?.name || 'Ordner'
                              : t.common.noFolder}
                          </span>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                        <DropdownMenuItem 
                          onClick={() => handleMoveToFolder(selectedContent.id, null)}
                          className="text-white hover:bg-[#0A1628] text-xs"
                        >
                          {t.common.noFolder}
                        </DropdownMenuItem>
                        {folders.map(folder => (
                          <DropdownMenuItem 
                            key={folder.id}
                            onClick={() => handleMoveToFolder(selectedContent.id, folder.id)}
                            className="text-white hover:bg-[#0A1628] text-xs"
                          >
                            <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
                            {folder.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <Button
                    onClick={handleSaveMeta}
                    disabled={isSaving}
                    className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
                  </Button>
                </div>
              )}

              {/* Comments Tab */}
              {activeTab === 'kommentare' && (
                <div className="space-y-4 flex flex-col h-full" data-testid="comments-tab">
                  {/* Comments List */}
                  <div className="flex-1 space-y-3 overflow-y-auto max-h-[400px]">
                    {isLoadingComments ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-[#00D9C8]" />
                      </div>
                    ) : comments.length > 0 ? (
                      comments.map(comment => (
                        <div 
                          key={comment.id} 
                          className="bg-[#1A2B42] p-3 rounded-lg group"
                          data-testid={`comment-${comment.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[#00D9C8]/20 flex items-center justify-center">
                                <span className="text-xs font-medium text-[#00D9C8]">
                                  {comment.user_name?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {comment.user_name || 'Benutzer'}
                                </p>
                                <p className="text-xs text-[#5A6C7D]">
                                  {formatCommentDate(comment.created_at)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-[#5A6C7D] hover:text-red-400 transition-all"
                              data-testid={`delete-comment-${comment.id}`}
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-sm text-[#A0AEC0] whitespace-pre-wrap pl-9">
                            {comment.text}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 bg-[#1A2B42] rounded-lg">
                        <MessageSquare className="w-10 h-10 mx-auto mb-3 text-[#5A6C7D]" />
                        <p className="text-[#A0AEC0] text-sm mb-1">{t.produktion.noComments}</p>
                        <p className="text-[#5A6C7D] text-xs">{t.produktion.firstComment}</p>
                      </div>
                    )}
                  </div>

                  {/* New Comment Input */}
                  <div className="border-t border-[#1A2B42] pt-4">
                    <div className="flex gap-2">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder={t.produktion.commentPlaceholder}
                        className="flex-1 min-h-[80px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-none text-sm"
                        data-testid="comment-input"
                      />
                    </div>
                    <Button
                      onClick={handlePostComment}
                      disabled={isPostingComment || !newComment.trim()}
                      className="w-full mt-2 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                      data-testid="post-comment-btn"
                    >
                      {isPostingComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t.produktion.addComment}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions Bar */}
            <div className="sticky bottom-0 bg-[#0A1628] border-t border-[#1A2B42] p-4 space-y-3">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleShareEmail}
                  className="flex-1 border-[#1A2B42] text-[#A0AEC0]"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {lang === 'de' ? 'Per E-Mail teilen' : 'Share via email'}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-[#1A2B42] text-[#A0AEC0]">
                      <Badge className={statusConfig[selectedContent.status]?.color}>
                        {statusConfig[selectedContent.status]?.label}
                      </Badge>
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                    {Object.entries(statusConfig)
                      .filter(([key]) => key !== 'video_generation' || selectedContent.platform === 'youtube')
                      .map(([key, config]) => (
                      <DropdownMenuItem 
                        key={key}
                        onClick={() => handleStatusChange(key)}
                        className="text-white hover:bg-[#0A1628]"
                      >
                        <Badge className={config.color}>{config.label}</Badge>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {selectedContent.platform === 'youtube' && selectedContent.status !== 'video_generation' ? (
                <Button
                  onClick={handleSubmitForGeneration}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                  data-testid="submit-for-generation-btn"
                >
                  <FaYoutube className="w-4 h-4 mr-2" />
                  Zur Generierung aufgeben
                </Button>
              ) : selectedContent.platform === 'youtube' && selectedContent.status === 'video_generation' ? (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-center">
                  <p className="text-sm text-purple-300 font-medium">{t.produktion.videoGenerating}</p>
                </div>
              ) : (
                <Button
                  onClick={handleApprove}
                  className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold"
                  data-testid="approve-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t.common.approve}
                </Button>
              )}

              <button
                onClick={handleDelete}
                className="w-full text-center text-sm text-[#5A6C7D] hover:text-red-400 py-2"
                data-testid="delete-btn"
              >
                <Trash className="w-4 h-4 inline mr-1" />
                {t.common.delete}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gallery Modal */}
      <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">{t.produktion.chooseFromGallery}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-4">
            {gallery.filter(g => g.file_type === 'image').map(item => (
              <button
                key={item.id}
                onClick={() => handleAttachGalleryImage(item.file_url)}
                className="aspect-square bg-[#0A1628] hover:ring-2 ring-[#00D9C8] transition-all overflow-hidden"
              >
                <img src={item.file_url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog - Legacy, kept for backwards compatibility */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628]">
          <DialogHeader>
            <DialogTitle className="text-white">Content freigeben</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#A0AEC0] mb-4">
              Soll dieser Content auch für eine andere Plattform aufbereitet werden?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {activePlatforms.filter(p => p.key !== selectedContent?.platform).map(platform => {
                const Icon = platform.icon;
                const isSelected = additionalPlatforms.includes(platform.key);
                const isVideoLocked = platform.key === 'youtube' && !subscription.features?.video;
                return (
                  <button
                    key={platform.key}
                    onClick={() => {
                      if (isVideoLocked) { setShowVideoGate(true); return; }
                      setAdditionalPlatforms(prev =>
                        isSelected ? prev.filter(p => p !== platform.key) : [...prev, platform.key]
                      );
                    }}
                    className={`flex items-center gap-2 p-3 transition-all ${
                      isVideoLocked
                        ? 'bg-[#0A1628]/50 border border-[#1A2B42] opacity-50 cursor-not-allowed'
                        : isSelected
                          ? 'bg-[#00D9C8]/10 border border-[#00D9C8]'
                          : 'bg-[#0A1628] border border-transparent'
                    }`}
                    data-testid={`additional-platform-${platform.key}${isVideoLocked ? '-locked' : ''}`}
                  >
                    <Icon className="w-4 h-4" style={{ color: platform.color }} />
                    <span className="text-sm text-white">{platform.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="border-[#1A2B42] text-[#A0AEC0]">
              {t.common.cancel}
            </Button>
            <Button onClick={confirmApprove} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]">
              {t.common.approve}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Modal - New Flow */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00D9C8]" />
              {t.produktion.schedulePost}
            </DialogTitle>
            {selectedContent && (() => {
              const cfg = getPlatformConfig(selectedContent.platform);
              const Icon = cfg.icon;
              return (
                <div className="flex items-center gap-2 mt-1">
                  <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  <span className="text-sm text-[#A0AEC0]">{cfg.name}</span>
                </div>
              );
            })()}
          </DialogHeader>
          
          {isLoadingSuggestion ? (
            <div className="py-8 flex flex-col items-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#00D9C8] mb-3" />
              <p className="text-[#A0AEC0]">KI berechnet optimale Posting-Zeit...</p>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {/* AI Suggestion Info */}
              <div className="bg-[#0A1628] p-3 border-l-2 border-[#00D9C8]">
                <p className="text-xs text-[#00D9C8] uppercase tracking-wider mb-1">KI-Empfehlung</p>
                <p className="text-sm text-[#A0AEC0]">{suggestedReason}</p>
              </div>
              
              {/* Date Input */}
              <div>
                <Label className="text-[#A0AEC0] text-xs mb-2 block">Datum</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
                />
              </div>
              
              {/* Time Input */}
              <div>
                <Label className="text-[#A0AEC0] text-xs mb-2 block">Uhrzeit</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
                />
              </div>
              
              {/* Preview */}
              <div className="bg-[#0A1628] p-3 space-y-2">
                <p className="text-xs text-[#5A6C7D] mb-1">Geplante Veröffentlichung</p>
                <p className="text-white font-medium">
                  {scheduleDate && scheduleTime && (
                    <>
                      {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleDateString('de-DE', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })} um {scheduleTime} Uhr
                    </>
                  )}
                </p>
                {selectedContent && (
                  <p className="text-xs text-[#00D9C8] flex items-center gap-1.5 pt-1">
                    <Send className="w-3.5 h-3.5" />
                    Automatisch bei {platforms.find(p => p.key === selectedContent.platform)?.label || selectedContent.platform} posten
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowScheduleModal(false)} 
              className="border-[#1A2B42] text-[#A0AEC0]"
            >
              {t.common.cancel}
            </Button>
            <Button 
              onClick={() => handleScheduleContent()}
              disabled={isScheduling || isLoadingSuggestion || !scheduleDate || !scheduleTime}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
            >
              {isScheduling ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {t.produktion.scheduleInCalendar}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-sm" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {t.produktion.bulkDeleteTitle}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#A0AEC0] text-sm py-2">
            {selectedIds.size} {lang === 'de' 
              ? `Beiträge werden permanent gelöscht. Das kann nicht rückgängig gemacht werden.`
              : `posts will be permanently deleted. This cannot be undone.`}
          </p>
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="flex-1 border-[#5A6C7D] text-[#A0AEC0]"
              data-testid="bulk-delete-cancel-btn"
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleBulkDelete}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              data-testid="bulk-delete-confirm-btn"
            >
              <Trash className="w-4 h-4 mr-1" />
              {t.produktion.deletePermanently}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Platform Adaptation Modal */}
      <Dialog open={showPlatformAdaptModal} onOpenChange={(open) => { if (!isAdapting && !open) { setShowPlatformAdaptModal(false); setScheduledContentId(null); } }}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md" data-testid="adapt-platforms-dialog">
          {isAdapting ? (
            <>
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-[#00D9C8]" />
                <p className="text-white font-medium text-center">
                  {lang === 'de' 
                    ? `Content wird für ${additionalPlatforms.length} ${additionalPlatforms.length === 1 ? 'Kanal' : 'Kanäle'} erstellt...`
                    : `Creating content for ${additionalPlatforms.length} channel${additionalPlatforms.length > 1 ? 's' : ''}...`}
                </p>
                <p className="text-[#5A6C7D] text-xs text-center">
                  {t.produktion.takeMoment}
                </p>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">
                  {t.produktion.adaptChannels}
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-[#A0AEC0] mb-4">
                  {lang === 'de'
                    ? 'Möchtest du diesen Content auch für andere Kanäle aufbereiten lassen? Die KI passt den Text automatisch an die jeweilige Plattform an.'
                    : 'Want to adapt this content for other channels? The AI will automatically adjust the text for each platform.'}
                </p>
                
                <div className="grid grid-cols-2 gap-2">
                  {activePlatforms.filter(p => p.key !== selectedContent?.platform).map(platform => {
                    const Icon = platform.icon;
                    const isSelected = additionalPlatforms.includes(platform.key);
                    const isVideoLocked = platform.key === 'youtube' && !subscription.features?.video;
                    return (
                      <button
                        key={platform.key}
                        data-testid={`adapt-platform-${platform.key}${isVideoLocked ? '-locked' : ''}`}
                        onClick={() => {
                          if (isVideoLocked) { setShowVideoGate(true); return; }
                          setAdditionalPlatforms(prev =>
                            isSelected ? prev.filter(p => p !== platform.key) : [...prev, platform.key]
                          );
                        }}
                        className={`flex items-center gap-2 p-3 transition-all ${
                          isVideoLocked
                            ? 'bg-[#0A1628]/50 border border-[#1A2B42] opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'bg-[#00D9C8]/10 border border-[#00D9C8]'
                              : 'bg-[#0A1628] border border-transparent hover:border-[#5A6C7D]'
                        }`}
                      >
                        <Icon className="w-4 h-4" style={{ color: platform.color }} />
                        <span className="text-sm text-white">{platform.label}</span>
                        {isSelected && <Check className="w-4 h-4 text-[#00D9C8] ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={skipPlatformAdaption}
                  className="border-[#1A2B42] text-[#A0AEC0] flex-1"
                >
                  {t.produktion.noThanks}
                </Button>
                <Button 
                  onClick={handleAdaptPlatforms}
                  disabled={additionalPlatforms.length === 0}
                  className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] flex-1"
                  data-testid="adapt-platforms-submit-btn"
                >
                  {additionalPlatforms.length > 0 
                    ? `${additionalPlatforms.length} ${t.produktion.channelsAdapt}`
                    : t.produktion.selectChannels
                  }
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Conflict Dialog - duplicate schedule detection */}
      <Dialog open={showConflictDialog} onOpenChange={(open) => { if (!open) { setShowConflictDialog(false); setConflictData(null); } }}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-md" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {t.produktion.postAlreadyScheduled}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[#A0AEC0] text-sm">
              {conflictData?.message || (lang === 'de' 
                ? 'Dieser Beitrag ist bereits für diesen Tag auf dieser Plattform eingeplant.' 
                : 'This post is already scheduled for this day on this platform.')}
            </p>
            {conflictData?.existingScheduledAt && (
              <p className="text-[#A0AEC0] text-xs mt-2">
                {t.produktion.scheduledAt}
                <span className="text-white font-medium">
                  {new Date(conflictData.existingScheduledAt).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </p>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleConflictReplace}
              disabled={isScheduling}
              className="bg-amber-500 hover:bg-amber-600 text-[#0A1628] w-full font-medium"
              data-testid="conflict-replace-btn"
            >
              {isScheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t.produktion.replaceScheduled}
            </Button>
            <Button
              onClick={handleConflictSaveDraft}
              variant="outline"
              className="border-[#5A6C7D] text-[#A0AEC0] hover:text-white w-full"
              data-testid="conflict-save-draft-btn"
            >
              {t.produktion.saveAsDraft}
            </Button>
            <Button
              onClick={() => { setShowConflictDialog(false); setConflictData(null); }}
              variant="ghost"
              className="text-[#5A6C7D] hover:text-[#A0AEC0] w-full"
              data-testid="conflict-cancel-btn"
            >
              {t.common.cancel}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expanded Content Modal */}
      <Dialog open={showExpandedModal} onOpenChange={setShowExpandedModal}>
        <DialogContent className="bg-[#0A1628] border-[#1A2B42] max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="expanded-modal">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedContent && getPlatformIcon(selectedContent?.platform)}
              <span className="font-mono text-[#00D9C8] text-sm">#{selectedContent?.content_number || '—'}</span>
              <span className="truncate">{selectedContent?.title}</span>
            </DialogTitle>
          </DialogHeader>
          {selectedContent && (
            <div className="flex-1 overflow-y-auto p-1 space-y-4" data-testid="expanded-modal-content">
              {/* Image */}
              {selectedContent.image_urls?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {selectedContent.image_urls.map((url, i) => (
                    <img key={i} src={url} alt="" className="w-full rounded-lg border border-[#1A2B42] object-cover aspect-video" />
                  ))}
                </div>
              )}

              {/* Pressemitteilung expanded */}
              {selectedContent.platform === 'pressemitteilung' && selectedContent.structured_content ? (
                <div className="space-y-4">
                  {PRESS_RELEASE_FIELDS.map(field => {
                    const val = editedStructuredContent[field.key] || '';
                    return (
                      <div key={field.key}>
                        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">{field.label}</Label>
                        {field.multiline ? (
                          <RichTextFieldEditor
                            content={val}
                            onChange={(html) => setEditedStructuredContent(prev => ({ ...prev, [field.key]: html }))}
                            maxChars={field.max}
                            placeholder={field.label}
                            minHeight={field.key === 'body_text' ? '350px' : '180px'}
                            testId={`expanded-press-${field.key}`}
                          />
                        ) : (
                          <Input
                            value={val}
                            onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                            data-testid={`expanded-press-${field.key}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : selectedContent.platform === 'blog' ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Titel</Label>
                    <Input value={blogTitle} onChange={(e) => setBlogTitle(e.target.value)} className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-lg font-semibold" />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Überschrift</Label>
                    <Input value={blogSubtitle} onChange={(e) => setBlogSubtitle(e.target.value)} className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white" />
                  </div>
                  <BlogEditor content={editedText} onChange={setEditedText} maxChars={getPlatformLimit(selectedContent.platform)} />
                </div>
              ) : selectedContent.platform === 'pinterest' ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Pin-Titel</Label>
                    <Input
                      value={editedStructuredContent.pin_title || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, pin_title: e.target.value.slice(0, 100) }))}
                      maxLength={100}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      data-testid="expanded-pinterest-title"
                    />
                    <span className="text-xs text-[#5A6C7D] mt-1 block">{(editedStructuredContent.pin_title || '').length} / 100</span>
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Pin-Beschreibung</Label>
                    <Textarea
                      value={editedStructuredContent.pin_description || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, pin_description: e.target.value.slice(0, 500) }))}
                      maxLength={500}
                      className="min-h-[250px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                      data-testid="expanded-pinterest-desc"
                    />
                    <span className="text-xs text-[#5A6C7D] mt-1 block">{(editedStructuredContent.pin_description || '').length} / 500</span>
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Link (optional)</Label>
                    <Input
                      value={editedStructuredContent.pin_link || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, pin_link: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      placeholder="https://..."
                      data-testid="expanded-pinterest-link"
                    />
                  </div>
                </div>
              ) : selectedContent.platform === 'youtube' ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Video-Titel</Label>
                    <Input
                      value={editedStructuredContent.youtube_title || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_title: e.target.value }))}
                      maxLength={100}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      data-testid="expanded-youtube-title"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Skript</Label>
                    <Textarea
                      value={editedStructuredContent.youtube_script || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_script: e.target.value }))}
                      className="min-h-[200px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                      data-testid="expanded-youtube-script"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">YouTube-Beschreibung</Label>
                    <Textarea
                      value={editedStructuredContent.youtube_description || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_description: e.target.value }))}
                      className="min-h-[150px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm leading-relaxed resize-y"
                      data-testid="expanded-youtube-desc"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Tags (SEO)</Label>
                    <Input
                      value={editedStructuredContent.youtube_tags || ''}
                      onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, youtube_tags: e.target.value }))}
                      className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                      placeholder="keyword1, keyword2"
                      data-testid="expanded-youtube-tags"
                    />
                  </div>
                </div>
              ) : selectedContent.platform?.startsWith('custom_') ? (
                <div className="space-y-4">
                  {(() => {
                    const cc = customChannels.find(c => c.id === selectedContent.platform);
                    if (!cc) return <p className="text-[#5A6C7D] text-sm">{lang === 'de' ? 'Kanal-Konfiguration nicht gefunden.' : 'Channel configuration not found.'}</p>;
                    const customFields = selectedContent.custom_fields || {};
                    return cc.fields.map((field) => {
                      const fieldValue = editedStructuredContent[`custom_${field.name}`] ?? customFields[field.name] ?? '';
                      return (
                        <div key={field.name}>
                          <div className="flex items-center justify-between mb-1.5">
                            <Label className="text-[#A0AEC0] text-xs">{field.name}</Label>
                            <span className="text-xs text-[#5A6C7D]">{fieldValue.length} / {field.maxLength}</span>
                          </div>
                          {field.maxLength > 200 ? (
                            <Textarea
                              value={fieldValue}
                              onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, [`custom_${field.name}`]: e.target.value }))}
                              style={{ minHeight: field.maxLength > 1000 ? '300px' : '180px' }}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white text-sm resize-y"
                              placeholder={field.name}
                              data-testid={`expanded-custom-${field.name}`}
                            />
                          ) : (
                            <Input
                              value={fieldValue}
                              onChange={(e) => setEditedStructuredContent(prev => ({ ...prev, [`custom_${field.name}`]: e.target.value }))}
                              className="bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white h-11 text-sm"
                              placeholder={field.name}
                              data-testid={`expanded-custom-${field.name}`}
                            />
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div>
                  <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Text</Label>
                  <Textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="min-h-[400px] bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white resize-y text-sm leading-relaxed"
                    data-testid="expanded-text-editor"
                  />
                  <div className="flex items-center justify-between text-xs px-2 py-1.5 mt-1 rounded bg-[#0A1628] text-[#5A6C7D]">
                    <span>{editedText.length.toLocaleString()} / {getPlatformLimit(selectedContent.platform).toLocaleString()} Zeichen</span>
                  </div>
                </div>
              )}

              {/* Tags in expanded */}
              {tags.length > 0 && (
                <div className="border-t border-[#1A2B42] pt-3">
                  <Label className="text-[#A0AEC0] text-xs mb-2 block">Tags</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map(tag => {
                      const isActive = (selectedContent.tags || []).includes(tag.name);
                      return (
                        <button key={tag.id} onClick={() => handleToggleTag(selectedContent.id, tag.name)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${isActive ? 'border-current opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`}
                          style={{ color: tag.color, backgroundColor: `${tag.color}15` }}>
                          {tag.name} {isActive && '✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-shrink-0 border-t border-[#1A2B42] pt-3">
            <Button variant="outline" onClick={() => setShowExpandedModal(false)} className="border-[#1A2B42] text-[#A0AEC0]">
              {t.common.close}
            </Button>
            <Button
              onClick={() => {
                if (selectedContent?.platform === 'pressemitteilung' || selectedContent?.platform === 'pinterest' || selectedContent?.platform === 'youtube') {
                  handleSaveStructuredContent();
                } else if (selectedContent?.platform?.startsWith('custom_')) {
                  handleSaveCustomFields();
                } else {
                  handleSaveText();
                }
                setShowExpandedModal(false);
              }}
              disabled={isSaving}
              className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
              data-testid="expanded-save-btn"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Manager Modal */}
      <Dialog open={showTagManager} onOpenChange={setShowTagManager}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-sm" data-testid="tag-manager-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#00D9C8]" />
              {t.produktion.manageTags}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Create new tag */}
            <div className="flex gap-2">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder={t.produktion.newTagName}
                className="flex-1 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm h-9"
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
                data-testid="new-tag-input"
              />
              <input
                type="color"
                value={newTagColor}
                onChange={(e) => setNewTagColor(e.target.value)}
                className="w-9 h-9 rounded cursor-pointer border-0 bg-transparent"
                data-testid="new-tag-color"
              />
              <Button onClick={handleCreateTag} size="sm" className="h-9 px-3 bg-[#00D9C8] text-[#0A1628]" data-testid="create-tag-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {/* Existing tags */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {tags.map(tag => (
                <div key={tag.id} className="flex items-center justify-between bg-[#0A1628] p-2.5 rounded" data-testid={`tag-item-${tag.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                    <span className="text-sm text-white">{tag.name}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="p-1 text-[#5A6C7D] hover:text-red-400 transition-colors"
                    data-testid={`delete-tag-${tag.id}`}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {tags.length === 0 && (
                <p className="text-[#5A6C7D] text-sm text-center py-4">{t.produktion.noTagsYet}</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Folder Move Modal */}
      <Dialog open={showFolderMoveModal} onOpenChange={(open) => { if (!open) { setShowFolderMoveModal(false); setMovingContentId(null); } }}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628] max-w-sm" data-testid="folder-move-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-[#00D9C8]" />
              {t.produktion.moveToFolder}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <button
              onClick={() => handleMoveToFolder(movingContentId, null)}
              className="w-full flex items-center gap-2 p-3 bg-[#0A1628] hover:bg-[#0A1628]/80 rounded text-sm text-[#A0AEC0] hover:text-white transition-colors"
              data-testid="move-no-folder"
            >
              <X className="w-4 h-4" />
              {t.produktion.removeFromFolder}
            </button>
            {folders.map(folder => (
              <button
                key={folder.id}
                onClick={() => handleMoveToFolder(movingContentId, folder.id)}
                className="w-full flex items-center gap-2 p-3 bg-[#0A1628] hover:bg-[#0A1628]/80 rounded text-sm text-white transition-colors"
                data-testid={`move-to-folder-${folder.id}`}
              >
                <FolderOpen className="w-4 h-4 text-[#00D9C8]" />
                {folder.name}
                <span className="ml-auto text-xs text-[#5A6C7D]">({folder.content_count})</span>
              </button>
            ))}
            {folders.length === 0 && (
              <p className="text-[#5A6C7D] text-sm text-center py-4">{t.produktion.noFoldersYet}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShowcaseProduktion;
